import { useState, useCallback, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { startOfDay, endOfDay, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subDays } from 'date-fns';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { hospitalAdminApi } from '@/api/hospitalAdmin';
import { HospitalAdminKpis } from '@/types/kpis';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { TimeIntervalFilter } from '@/components/TimeIntervalFilter';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { format } from 'date-fns';
import {
  FileText,
  IndianRupee,
  Calendar,
  FlaskConical,
  Layers,
  Users,
  Printer,
  Download,
  Pill,
  Receipt,
} from 'lucide-react';

import { Line, Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { businessReportsApi, type BillingReportType, type BillWiseCategory, type DashboardGranularity, type HospitalPnlMomResponse, type HospitalPnlResponse, type PatientMasterScope, type SalesAttributionRow, type SalesAttributionDimension, type ServiceTypeKey } from '@/api/businessReports';
import { CashFlowReportPanel, downloadCashFlowCsv } from '@/components/business-reports/CashFlowReportPanel';
import { PnlEditableCell } from '@/components/business-reports/PnlEditableCell';
import { PnlExpenseBreakdownHover } from '@/components/business-reports/PnlExpenseBreakdownHover';
import { PnlIpdDirectCostBreakdownHover } from '@/components/business-reports/PnlIpdDirectCostBreakdownHover';
import { PnlAestheticProceduresHover } from '@/components/business-reports/PnlAestheticProceduresHover';
import { isPnlAestheticProceduresLineId } from '@/lib/pnlAestheticProcedures';
import { isPnlIpdDirectCostLineId } from '@/lib/pnlIpdDirectLines';
import { isPnlCentralExpenseLineId } from '@/lib/pnlExpenseLines';
import { BusinessReportsDashboard } from '@/components/business-reports/BusinessReportsDashboard';
import { MonthlyBusinessPerformanceReport } from '@/components/business-reports/MonthlyBusinessPerformanceReport';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { defaultFinancialReportHospitalId } from '@/constants/pnlDefaultScope';
import { superAdminApi } from '@/api/superAdmin';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

type ReportSummaryTile = { label: string; value: number; format?: 'currency' | 'integer' | 'decimal' };

function ReportSummaryTiles({
  tiles,
  variant = 'default',
}: {
  tiles: ReportSummaryTile[];
  variant?: 'default' | 'investor';
}) {
  if (!tiles.length) return null;
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
      {tiles.map((t) => (
        <div
          key={t.label}
          className={cn(
            'p-3',
            variant === 'investor'
              ? 'rounded-xl border border-artis-brown/15 bg-gradient-to-br from-white to-artis-cream p-3 shadow-sm'
              : 'rounded-lg border bg-muted/20'
          )}
        >
          <p className={cn('text-xs', variant === 'investor' ? 'text-artis-slate' : 'text-slate-500')}>{t.label}</p>
          <p className={cn('text-lg font-semibold mt-1 tabular-nums', variant === 'investor' ? 'text-artis-brown-dark' : 'text-slate-900')}>
            {t.format === 'currency'
              ? `₹${t.value.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`
              : t.format === 'decimal'
                ? t.value.toLocaleString('en-IN', { maximumFractionDigits: 2 })
                : t.value.toLocaleString('en-IN')}
          </p>
        </div>
      ))}
    </div>
  );
}

function formatReportServiceDay(serviceDay: string | undefined, fallbackIso: string): string {
  if (serviceDay && /^\d{4}-\d{2}-\d{2}$/.test(serviceDay)) {
    const [y, m, d] = serviceDay.split('-');
    return `${d}/${m}/${y}`;
  }
  return format(new Date(fallbackIso), 'dd/MM/yyyy');
}

const REPORT_IST = 'Asia/Kolkata';

/** Bill / payment lines: always show IST wall clock (not browser local). */
function formatReportDateTimeIst(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: REPORT_IST,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(d);
}

function formatServiceMonthLabel(ym: string): string {
  if (!/^\d{4}-\d{2}$/.test(ym)) return ym;
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleString('en-IN', { month: 'long', year: 'numeric' });
}

function formatReportBucketLabel(bucketIso: string, bucketMode: 'day' | 'month'): string {
  const d = new Date(bucketIso);
  if (Number.isNaN(d.getTime())) return String(bucketIso ?? '');
  return bucketMode === 'month' ? format(d, 'MMM yyyy') : format(d, 'dd MMM');
}

function formatPatientSourceLabel(source: string | null | undefined): string {
  const raw = String(source ?? '').trim();
  return raw ? raw.replace(/_/g, ' ') : 'Unknown';
}

/** Amount cell with optional Paid/Unpaid label under the value (IPD profitability). */
function ProfitabilityAmountCell({
  amount,
  status,
  strong = false,
}: {
  amount: number;
  status?: string | null;
  strong?: boolean;
}) {
  const n = Number(amount || 0);
  const showStatus = n > 0.009 && (status === 'Paid' || status === 'Unpaid');
  return (
    <td className={`p-2 text-right ${strong ? 'font-semibold' : ''}`}>
      <div>₹{n.toLocaleString()}</div>
      {showStatus ? (
        <div
          className={
            status === 'Paid'
              ? 'text-xs font-medium text-emerald-700'
              : 'text-xs font-medium text-amber-700'
          }
        >
          {status}
        </div>
      ) : null}
    </td>
  );
}

type IntervalOption = 'today' | 'yesterday' | 'this_week' | 'this_month' | 'custom_month' | 'custom_year';
const PAYMENT_SERVICE_TYPES: ServiceTypeKey[] = [
  'OPD',
  'IPD',
  'Pharma',
  'Lab',
  'DayCare',
  'AdditionalServices',
  'Emergency',
];

const SERVICE_TYPE_LABELS: Record<ServiceTypeKey, string> = {
  OPD: 'OPD',
  IPD: 'IPD',
  Pharma: 'Pharma',
  Lab: 'Lab',
  DayCare: 'DayCare',
  AdditionalServices: 'Additional Services',
  Emergency: 'Emergency',
};

type ReportType =
  | 'summary'
  | 'revenue'
  | 'appointments'
  | 'lab'
  | 'revenue_breakdown'
  | 'pharma_revenue'
  | 'pharma_consumption'
  | 'lab_billing'
  | 'gst_filing'
  | 'additional_services'
  | 'patients'
  | 'patient_billing'
  | 'expenses'
  | 'monthly_expenses'
  | 'monthly_performance'
  | 'pnl'
  | 'cash_flow'
  | 'ipd_profitability'
  | 'plastic_surgery_revenue'
  | 'plastic_surgery_profitability'
  | 'opd_payment_lines'
  | 'daycare_payment_lines'
  | 'sales_attribution'
  | 'bill_wise'
  | 'patient_master';

/** Reports that load GET /hospital-admin/kpis-by-interval. */
const KPI_REPORT_TYPES: ReportType[] = ['summary', 'revenue', 'appointments', 'lab'];

/** Investor report keys that grant access to the KPI endpoint. */
const INVESTOR_KPI_REPORT_KEYS = ['summary', 'revenue', 'appointments', 'lab'] as const;

export default function BusinessReports({
  salesOnly = false,
  summaryOnly = false,
}: {
  salesOnly?: boolean;
  summaryOnly?: boolean;
}) {
  const location = useLocation();
  const portalBase = useMemo(() => {
    const m = location.pathname.match(/^\/(hospital-admin|super-admin|receptionist)/);
    return m ? `/${m[1]}` : '/hospital-admin';
  }, [location.pathname]);

  const [startDate, setStartDate] = useState<Date>();
  const [endDate, setEndDate] = useState<Date>();
  const [selectedInterval, setSelectedInterval] = useState<IntervalOption>('this_month');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [reportType, setReportType] = useState<ReportType>(
    salesOnly ? 'sales_attribution' : 'summary'
  );
  const [bucket, setBucket] = useState<'day' | 'month'>('day');
  const [chartGranularity, setChartGranularity] = useState<DashboardGranularity>('day');
  const [billingDetailType, setBillingDetailType] = useState<BillingReportType>('All');
  const [billingDetailView, setBillingDetailView] = useState<'summary' | 'items'>('summary');
  const [expenseServiceMonth, setExpenseServiceMonth] = useState('');
  const [monthlyExpenseServiceMonth, setMonthlyExpenseServiceMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [performanceMonth, setPerformanceMonth] = useState(() => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [pnlMode, setPnlMode] = useState<'MTD' | 'YTD' | 'MOM'>('MTD');
  const [pnlMonth, setPnlMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
  const [pnlFyStartYear, setPnlFyStartYear] = useState(() => {
    const d = new Date();
    const m = d.getMonth(); // 0-indexed
    return m >= 3 ? d.getFullYear() : d.getFullYear() - 1;
  });
  const [billWiseCategory, setBillWiseCategory] = useState<BillWiseCategory>('All');
  const [patientMasterScope, setPatientMasterScope] = useState<PatientMasterScope>('active');
  const [salesDrilldown, setSalesDrilldown] = useState<{
    dimension: SalesAttributionDimension;
    name: string;
    label: string;
  } | null>(null);
  const { user, activeHospitalId } = useAuth();
  const queryClient = useQueryClient();
  const [selectedHospitalId, setSelectedHospitalId] = useState<string>('');
  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const isChainAdmin = user?.role === 'HOSPITAL_ADMIN' && user?.adminScope === 'CHAIN';
  const isInvestor = user?.role === 'INVESTOR';
  const canEditIpdPlasticSurgery =
    user?.role === 'SUPER_ADMIN' ||
    user?.role === 'HOSPITAL_ADMIN' ||
    user?.role === 'BUSINESS_ANALYST';
  const investorReportKeys = (user?.investorReportKeys ?? []) as ReportType[];
  const investorHasChainScope = isInvestor && (user?.hospitals?.length ?? 0) > 1;
  const showHospitalFilter = isSuperAdmin || isChainAdmin || investorHasChainScope;
  const summaryTilesVariant = isInvestor ? ('investor' as const) : ('default' as const);
  const investorActionBtnClass = isInvestor
    ? 'border-artis-gold/45 text-artis-brown hover:bg-artis-gold hover:text-white hover:border-artis-gold'
    : '';
  /** P&L / Cash Flow — chain/super, or investor when explicitly granted. */
  const canViewPnl =
    isSuperAdmin ||
    isChainAdmin ||
    (isInvestor && investorReportKeys.includes('pnl'));
  const canViewCashFlow =
    isSuperAdmin ||
    isChainAdmin ||
    (isInvestor && investorReportKeys.includes('cash_flow'));
  /** Manual P&L / Cash Flow overrides — chain/super only. */
  const canEditFinancialStatements = isSuperAdmin || isChainAdmin;
  const canAccessReportType = (type: ReportType) => {
    if (summaryOnly) return type === 'summary';
    if (salesOnly) return type === 'sales_attribution';
    return !isInvestor || investorReportKeys.includes(type);
  };
  const investorCanFetchKpis =
    !isInvestor ||
    investorReportKeys.some((k) =>
      (INVESTOR_KPI_REPORT_KEYS as readonly string[]).includes(k)
    );
  const reportUsesKpis = KPI_REPORT_TYPES.includes(reportType);

  const chainHospitals = user?.hospitals ?? [];
  const isChainScoped = isChainAdmin || investorHasChainScope;
  const defaultFinancialHospitalId = useMemo(
    () =>
      defaultFinancialReportHospitalId({
        isSuperAdmin,
        isChainScoped,
        activeHospitalId,
        fallbackHospitalId: chainHospitals[0]?.id ?? null,
      }),
    [isSuperAdmin, isChainScoped, activeHospitalId, chainHospitals]
  );

  /**
   * `hospitalId` sent on every report API call.
   * - Super admin: specific id | undefined (all)
   * - Chain admin: specific id | "all" (consolidated) — MUST always be explicit so
   *   the axios interceptor doesn't silently override it with activeHospitalId.
   * - Sub-admin / staff: undefined (backend scopes to their own hospital)
   */
  /** Chain/investor: explicit `all` for consolidated; otherwise selected site or default unit. */
  const reportHospitalId: string | undefined = isSuperAdmin
    ? selectedHospitalId || undefined
    : isChainScoped
      ? selectedHospitalId === 'all'
        ? 'all'
        : selectedHospitalId || defaultFinancialHospitalId
      : undefined;

  const { data: hospitals } = useQuery({
    queryKey: ['super-admin-hospitals'],
    queryFn: () => superAdminApi.getHospitals(),
    enabled: isSuperAdmin,
  });

  useEffect(() => {
    if (!isChainScoped) return;
    if (selectedHospitalId) return;
    setSelectedHospitalId(defaultFinancialHospitalId);
  }, [isChainScoped, activeHospitalId, selectedHospitalId, defaultFinancialHospitalId]);

  useEffect(() => {
    if (summaryOnly && reportType !== 'summary') {
      setReportType('summary');
    }
  }, [summaryOnly, reportType]);

  useEffect(() => {
    if (!isInvestor || investorReportKeys.length === 0) return;
    if (!investorReportKeys.includes(reportType)) {
      setReportType(investorReportKeys[0]);
    }
  }, [isInvestor, reportType, investorReportKeys]);

  useEffect(() => {
    if ((reportType === 'pnl' || reportType === 'cash_flow') && !canViewPnl && !canViewCashFlow) {
      setReportType(salesOnly ? 'sales_attribution' : investorReportKeys[0] ?? 'summary');
    } else if (reportType === 'pnl' && !canViewPnl) {
      setReportType(salesOnly ? 'sales_attribution' : investorReportKeys[0] ?? 'summary');
    } else if (reportType === 'cash_flow' && !canViewCashFlow) {
      setReportType(salesOnly ? 'sales_attribution' : investorReportKeys[0] ?? 'summary');
    }
  }, [reportType, canViewPnl, canViewCashFlow, salesOnly, isInvestor, investorReportKeys]);

  const handleTimeIntervalChange = useCallback((start: Date, end: Date) => {
    setStartDate(start);
    setEndDate(end);
  }, []);

  const handleIntervalChange = useCallback((interval: IntervalOption) => {
    setSelectedInterval(interval);
  }, []);

  const handleMonthChange = useCallback((month: number) => {
    setSelectedMonth(month);
  }, []);

  const handleYearChange = useCallback((year: number) => {
    setSelectedYear(year);
  }, []);

  // Set initial date range on mount based on selected interval
  useEffect(() => {
    const now = new Date();
    let start: Date;
    let end: Date;
    switch (selectedInterval) {
      case 'today':
        start = startOfDay(now);
        end = endOfDay(now);
        break;
      case 'yesterday': {
        const yesterday = subDays(now, 1);
        start = startOfDay(yesterday);
        end = endOfDay(yesterday);
        break;
      }
      case 'this_week':
        start = startOfWeek(now, { weekStartsOn: 1 });
        end = endOfWeek(now, { weekStartsOn: 1 });
        break;
      case 'this_month':
        start = startOfMonth(now);
        end = endOfMonth(now);
        break;
      case 'custom_month': {
        const d = new Date(selectedYear, selectedMonth);
        start = startOfMonth(d);
        end = endOfMonth(d);
        break;
      }
      case 'custom_year':
        start = new Date(selectedYear, 0, 1);
        end = new Date(selectedYear, 11, 31, 23, 59, 59, 999);
        break;
      default:
        start = startOfMonth(now);
        end = endOfMonth(now);
    }
    setStartDate(start);
    setEndDate(end);
  }, []);

  const { data: kpis, isLoading, error } = useQuery<HospitalAdminKpis>({
    queryKey: [
      'hospital-business-reports',
      startDate?.toISOString(),
      endDate?.toISOString(),
      reportHospitalId ?? 'all'
    ],
    queryFn: async () => {
      if (startDate && endDate) {
        return hospitalAdminApi.getKpisByDate(
          startDate.toISOString(),
          endDate.toISOString(),
          reportHospitalId
        );
      }
      const now = new Date();
      const start = new Date(now);
      start.setHours(0, 0, 0, 0);
      const end = new Date(now);
      end.setHours(23, 59, 59, 999);
      return hospitalAdminApi.getKpisByDate(start.toISOString(), end.toISOString(), reportHospitalId);
    },
    enabled: !salesOnly && reportUsesKpis && investorCanFetchKpis,
  });

  const fromIso = startDate?.toISOString();
  const toIso = endDate?.toISOString();

  const {
    data: dashboardAnalytics,
    isLoading: loadingDashboard,
    isError: dashboardError,
    error: dashboardErrorRaw,
  } = useQuery({
    queryKey: ['dashboard-analytics', fromIso, toIso, chartGranularity, reportHospitalId ?? 'all'],
    queryFn: async () => {
      if (!fromIso || !toIso) return null;
      return businessReportsApi.dashboardAnalytics({
        from: fromIso,
        to: toIso,
        granularity: chartGranularity,
        hospitalId: reportHospitalId ?? undefined,
      });
    },
    enabled: !!fromIso && !!toIso && reportType === 'summary',
    retry: 1,
  });

  const dashboardErrorMessage =
    (dashboardErrorRaw as { response?: { data?: { message?: string }; status?: number } })?.response
      ?.data?.message ||
    ((dashboardErrorRaw as { response?: { status?: number } })?.response?.status === 404
      ? 'Dashboard analytics endpoint not found (404).'
      : null);

  const { data: paymentByServiceType } = useQuery({
    queryKey: ['payment-by-service-type', fromIso, toIso, reportHospitalId ?? 'all'],
    queryFn: async () => {
      if (!fromIso || !toIso) return null;
      return businessReportsApi.paymentByServiceType({
        from: fromIso,
        to: toIso,
        hospitalId: reportHospitalId ?? undefined,
      });
    },
    enabled: !!fromIso && !!toIso && (reportType === 'summary' || reportType === 'revenue'),
  });

  const { data: revenueBreakdown } = useQuery({
    queryKey: ['revenue-breakdown', fromIso, toIso, bucket, reportHospitalId],
    queryFn: async () => {
      if (!fromIso || !toIso) return null;
      return businessReportsApi.revenueBreakdown({ from: fromIso, to: toIso, bucket, hospitalId: reportHospitalId });
    },
    enabled: !!fromIso && !!toIso && reportType === 'revenue_breakdown',
  });

  const { data: newPatients } = useQuery({
    queryKey: ['new-patients-report', fromIso, toIso, bucket, reportHospitalId],
    queryFn: async () => {
      if (!fromIso || !toIso) return null;
      return businessReportsApi.newPatients({ from: fromIso, to: toIso, bucket, hospitalId: reportHospitalId });
    },
    enabled: !!fromIso && !!toIso && reportType === 'patients',
  });

  const { data: patientSources } = useQuery({
    queryKey: ['patient-sources-report', fromIso, toIso, reportHospitalId],
    queryFn: async () => {
      if (!fromIso || !toIso) return null;
      return businessReportsApi.patientSources({ from: fromIso, to: toIso, hospitalId: reportHospitalId });
    },
    enabled: !!fromIso && !!toIso && reportType === 'patients',
  });

  const { data: patientRevenueAll, isLoading: loadingPatientRevenue } = useQuery({
    queryKey: ['patient-revenue-all', fromIso, toIso, reportHospitalId],
    queryFn: async () => {
      if (!fromIso || !toIso) return null;
      return businessReportsApi.patientRevenueAll({ from: fromIso, to: toIso, hospitalId: reportHospitalId });
    },
    enabled: !!fromIso && !!toIso && reportType === 'patients',
  });

  const { data: patientBillingDetail, isLoading: loadingBillingDetail } = useQuery({
    queryKey: ['patient-billing-detail', fromIso, toIso, billingDetailType, reportHospitalId],
    queryFn: async () => {
      if (!fromIso || !toIso) return null;
      return businessReportsApi.patientBillingDetail({ from: fromIso, to: toIso, type: billingDetailType, hospitalId: reportHospitalId });
    },
    enabled: !!fromIso && !!toIso && reportType === 'patient_billing' && billingDetailView === 'summary',
  });

  const { data: patientBillingDetailItems, isLoading: loadingBillingDetailItems } = useQuery({
    queryKey: ['patient-billing-detail-items', fromIso, toIso, billingDetailType, reportHospitalId],
    queryFn: async () => {
      if (!fromIso || !toIso) return null;
      return businessReportsApi.patientBillingDetailItems({ from: fromIso, to: toIso, type: billingDetailType, hospitalId: reportHospitalId });
    },
    enabled: !!fromIso && !!toIso && reportType === 'patient_billing' && billingDetailView === 'items',
  });

  const { data: ipdProfitability, isLoading: loadingIpdProfitability } = useQuery({
    queryKey: ['ipd-profitability', fromIso, toIso, reportHospitalId ?? 'all'],
    queryFn: async () => {
      if (!fromIso || !toIso) return null;
      return businessReportsApi.ipdDischargedProfitability({
        from: fromIso,
        to: toIso,
        hospitalId: reportHospitalId ?? undefined,
      });
    },
    enabled: !!fromIso && !!toIso && reportType === 'ipd_profitability',
  });

  const updateIpdPlasticSurgeryMut = useMutation({
    mutationFn: ({ admissionId, isPlasticSurgery }: { admissionId: string; isPlasticSurgery: boolean }) =>
      businessReportsApi.updateIpdPlasticSurgeryFlag(admissionId, isPlasticSurgery),
    onSuccess: (data) => {
      queryClient.setQueryData(
        ['ipd-profitability', fromIso, toIso, reportHospitalId ?? 'all'],
        (prev: { rows?: Array<{ admissionId: string; isPlasticSurgery?: boolean }> } | null) => {
          if (!prev?.rows) return prev;
          return {
            ...prev,
            rows: prev.rows.map((row) =>
              row.admissionId === data.admissionId
                ? { ...row, isPlasticSurgery: data.isPlasticSurgery }
                : row
            ),
          };
        }
      );
      void queryClient.invalidateQueries({ queryKey: ['plastic-surgery-revenue'] });
      void queryClient.invalidateQueries({ queryKey: ['plastic-surgery-profitability'] });
      toast.success(data.isPlasticSurgery ? 'Marked as Plastic Surgery' : 'Marked as not Plastic Surgery');
    },
    onError: (err: unknown) => {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ||
        'Failed to update Plastic Surgery flag';
      toast.error(message);
    },
  });

  const { data: plasticSurgeryRevenue, isLoading: loadingPlasticSurgeryRevenue } = useQuery({
    queryKey: ['plastic-surgery-revenue', fromIso, toIso, reportHospitalId ?? 'all'],
    queryFn: async () => {
      if (!fromIso || !toIso) return null;
      return businessReportsApi.plasticSurgeryRevenue({
        from: fromIso,
        to: toIso,
        hospitalId: reportHospitalId ?? undefined,
      });
    },
    enabled: !!fromIso && !!toIso && reportType === 'plastic_surgery_revenue',
  });

  const { data: plasticSurgeryProfitability, isLoading: loadingPlasticSurgeryProfitability } = useQuery({
    queryKey: ['plastic-surgery-profitability', fromIso, toIso, reportHospitalId ?? 'all'],
    queryFn: async () => {
      if (!fromIso || !toIso) return null;
      return businessReportsApi.plasticSurgeryProfitability({
        from: fromIso,
        to: toIso,
        hospitalId: reportHospitalId ?? undefined,
      });
    },
    enabled: !!fromIso && !!toIso && reportType === 'plastic_surgery_profitability',
  });

  const { data: expenseReport } = useQuery({
    queryKey: ['expense-report', fromIso, toIso, reportHospitalId ?? 'all', expenseServiceMonth || 'all'],
    queryFn: async () => {
      if (!fromIso || !toIso) return null;
      return businessReportsApi.expensesReport({
        from: fromIso,
        to: toIso,
        hospitalId: reportHospitalId,
        ...(expenseServiceMonth.trim() ? { serviceMonth: expenseServiceMonth.trim() } : {}),
        includeLines: true,
      });
    },
    enabled: !!fromIso && !!toIso && reportType === 'expenses',
  });

  const { data: monthlyExpenseReport } = useQuery({
    queryKey: [
      'monthly-expense-report',
      monthlyExpenseServiceMonth,
      reportHospitalId ?? 'all',
    ],
    queryFn: async () => {
      if (!monthlyExpenseServiceMonth.trim()) return null;
      return businessReportsApi.monthlyExpensesByServiceMonth({
        serviceMonth: monthlyExpenseServiceMonth.trim(),
        hospitalId: reportHospitalId,
      });
    },
    enabled: !!monthlyExpenseServiceMonth.trim() && reportType === 'monthly_expenses',
  });

  const { data: monthlyPerformanceReport, isLoading: monthlyPerformanceLoading } = useQuery({
    queryKey: ['monthly-business-performance', performanceMonth, reportHospitalId ?? 'all'],
    queryFn: async () => {
      if (!performanceMonth.trim()) return null;
      return businessReportsApi.monthlyBusinessPerformance({
        month: performanceMonth.trim(),
        hospitalId: reportHospitalId,
      });
    },
    enabled: !!performanceMonth.trim() && reportType === 'monthly_performance',
  });

  const pnlQueryKey = useMemo(
    () => [
      'hospital-pnl',
      pnlMode,
      pnlMode === 'MTD' ? pnlMonth : pnlFyStartYear,
      reportHospitalId ?? 'all',
    ],
    [pnlMode, pnlMonth, pnlFyStartYear, reportHospitalId]
  );

  const { data: pnlReport, isLoading: loadingPnl, isError: pnlError, error: pnlQueryError } = useQuery({
    queryKey: pnlQueryKey,
    queryFn: async () => {
      return businessReportsApi.hospitalPnl({
        mode: pnlMode,
        month: pnlMode === 'MTD' ? pnlMonth : undefined,
        fy: pnlMode === 'YTD' || pnlMode === 'MOM' ? pnlFyStartYear : undefined,
        hospitalId: reportHospitalId,
      });
    },
    enabled: reportType === 'pnl' && canViewPnl,
  });

  const cfQueryKey = useMemo(
    () => [
      'hospital-cash-flow',
      pnlMode,
      pnlMode === 'MTD' ? pnlMonth : pnlFyStartYear,
      reportHospitalId ?? 'all',
    ],
    [pnlMode, pnlMonth, pnlFyStartYear, reportHospitalId]
  );

  const {
    data: cashFlowReport,
    isLoading: loadingCashFlow,
    isError: cashFlowError,
    error: cashFlowQueryError,
  } = useQuery({
    queryKey: cfQueryKey,
    queryFn: async () =>
      businessReportsApi.hospitalCashFlow({
        mode: pnlMode,
        month: pnlMode === 'MTD' ? pnlMonth : undefined,
        fy: pnlMode === 'YTD' || pnlMode === 'MOM' ? pnlFyStartYear : undefined,
        hospitalId: reportHospitalId,
      }),
    enabled: reportType === 'cash_flow' && canViewCashFlow,
  });

  const { data: opdPaymentLinesData, isLoading: loadingOpdPaymentLines } = useQuery({
    queryKey: ['opd-payment-lines', fromIso, toIso, reportHospitalId ?? 'all'],
    queryFn: async () => {
      if (!fromIso || !toIso) return null;
      return businessReportsApi.opdPaymentLines({
        from: fromIso,
        to: toIso,
        hospitalId: reportHospitalId ?? undefined,
      });
    },
    enabled: !!fromIso && !!toIso && reportType === 'opd_payment_lines',
  });

  const { data: dayCarePaymentLinesData, isLoading: loadingDayCarePaymentLines } = useQuery({
    queryKey: ['daycare-payment-lines', fromIso, toIso, reportHospitalId ?? 'all'],
    queryFn: async () => {
      if (!fromIso || !toIso) return null;
      return businessReportsApi.dayCarePaymentLines({
        from: fromIso,
        to: toIso,
        hospitalId: reportHospitalId ?? undefined,
      });
    },
    enabled: !!fromIso && !!toIso && reportType === 'daycare_payment_lines',
  });

  const { data: salesAttribution, isLoading: loadingSalesAttribution } = useQuery({
    queryKey: ['sales-attribution', fromIso, toIso, reportHospitalId ?? 'all'],
    queryFn: async () => {
      if (!fromIso || !toIso) return null;
      return businessReportsApi.salesAttributionReport({
        from: fromIso,
        to: toIso,
        hospitalId: reportHospitalId ?? undefined,
      });
    },
    enabled: !!fromIso && !!toIso && reportType === 'sales_attribution',
  });

  const { data: salesAttributionCases, isLoading: loadingSalesAttributionCases } = useQuery({
    queryKey: [
      'sales-attribution-cases',
      fromIso,
      toIso,
      reportHospitalId ?? 'all',
      salesDrilldown?.dimension,
      salesDrilldown?.name,
    ],
    queryFn: async () => {
      if (!fromIso || !toIso || !salesDrilldown) return null;
      return businessReportsApi.salesAttributionCases({
        from: fromIso,
        to: toIso,
        hospitalId: reportHospitalId ?? undefined,
        dimension: salesDrilldown.dimension,
        name: salesDrilldown.name,
      });
    },
    enabled: !!fromIso && !!toIso && !!salesDrilldown,
  });

  const { data: pharmaRevenueLines, isLoading: loadingPharmaRevenueLines } = useQuery({
    queryKey: ['pharma-revenue-lines', fromIso, toIso, reportHospitalId ?? 'all'],
    queryFn: async () => {
      if (!fromIso || !toIso) return null;
      return businessReportsApi.pharmaRevenueLines({
        from: fromIso,
        to: toIso,
        hospitalId: reportHospitalId ?? undefined,
      });
    },
    enabled: !!fromIso && !!toIso && reportType === 'pharma_revenue',
  });

  const { data: pharmaConsumptionByDrug, isLoading: loadingPharmaConsumption } = useQuery({
    queryKey: ['pharma-consumption-by-drug', fromIso, toIso, reportHospitalId ?? 'all'],
    queryFn: async () => {
      if (!fromIso || !toIso) return null;
      return businessReportsApi.pharmaConsumptionByDrug({
        from: fromIso,
        to: toIso,
        hospitalId: reportHospitalId ?? undefined,
      });
    },
    enabled: !!fromIso && !!toIso && reportType === 'pharma_consumption',
  });

  const { data: labBillingLines, isLoading: loadingLabBillingLines } = useQuery({
    queryKey: ['lab-billing-lines', fromIso, toIso, reportHospitalId ?? 'all'],
    queryFn: async () => {
      if (!fromIso || !toIso) return null;
      return businessReportsApi.labBillingLines({
        from: fromIso,
        to: toIso,
        hospitalId: reportHospitalId ?? undefined,
      });
    },
    enabled: !!fromIso && !!toIso && reportType === 'lab_billing',
  });

  const { data: gstFilingReport, isLoading: loadingGstFiling } = useQuery({
    queryKey: ['gst-filing-lines', fromIso, toIso, reportHospitalId ?? 'all'],
    queryFn: async () => {
      if (!fromIso || !toIso) return null;
      return businessReportsApi.gstFilingLines({
        from: fromIso,
        to: toIso,
        hospitalId: reportHospitalId ?? undefined,
      });
    },
    enabled: !!fromIso && !!toIso && reportType === 'gst_filing',
  });

  const { data: additionalServicesBilling, isLoading: loadingAdditionalServicesBilling } = useQuery({
    queryKey: ['additional-services-billing', fromIso, toIso, reportHospitalId ?? 'all'],
    queryFn: async () => {
      if (!fromIso || !toIso) return null;
      return businessReportsApi.additionalServicesBilling({
        from: fromIso,
        to: toIso,
        hospitalId: reportHospitalId ?? undefined,
      });
    },
    enabled: !!fromIso && !!toIso && reportType === 'additional_services',
  });

  const { data: billWiseReport, isLoading: loadingBillWise } = useQuery({
    queryKey: ['bill-wise-report', fromIso, toIso, billWiseCategory, reportHospitalId ?? 'all'],
    queryFn: async () => {
      if (!fromIso || !toIso) return null;
      return businessReportsApi.billWiseReport({
        from: fromIso,
        to: toIso,
        category: billWiseCategory,
        hospitalId: reportHospitalId ?? undefined,
      });
    },
    enabled: !!fromIso && !!toIso && reportType === 'bill_wise',
  });

  const patientMasterEnabled =
    reportType === 'patient_master' && (patientMasterScope === 'all' || (!!fromIso && !!toIso));

  const { data: patientMasterData, isLoading: loadingPatientMaster } = useQuery({
    queryKey: ['patient-master-report', patientMasterScope, fromIso, toIso, reportHospitalId ?? 'all'],
    queryFn: async () =>
      businessReportsApi.patientMasterReport({
        scope: patientMasterScope,
        ...(fromIso && toIso ? { from: fromIso, to: toIso } : {}),
        hospitalId: reportHospitalId ?? undefined,
      }),
    enabled: patientMasterEnabled,
  });

  const ipdProfitabilitySummaryTiles = useMemo((): ReportSummaryTile[] => {
    const rows = ipdProfitability?.rows ?? [];
    if (!rows.length) return [];
    const sum = (key: string) => rows.reduce((s, r: Record<string, number>) => s + Number(r[key] ?? 0), 0);
    return [
      { label: 'Discharged IPDs', value: rows.length, format: 'integer' },
      { label: 'Total revenue', value: sum('totalRevenue'), format: 'currency' },
      { label: 'Total expenses', value: sum('totalExpense'), format: 'currency' },
      { label: 'Net profit', value: sum('profit'), format: 'currency' },
    ];
  }, [ipdProfitability?.rows]);

  const plasticSurgeryRevenueSummaryTiles = useMemo((): ReportSummaryTile[] => {
    const t = plasticSurgeryRevenue?.totals;
    if (!t) return [];
    return [
      { label: 'Patients', value: plasticSurgeryRevenue?.rows?.length ?? 0, format: 'integer' },
      { label: 'OPD', value: t.opd, format: 'currency' },
      { label: 'Lab', value: t.lab, format: 'currency' },
      { label: 'Pharma', value: t.pharma, format: 'currency' },
      { label: 'IPD', value: t.ipd, format: 'currency' },
      { label: 'Total revenue', value: t.total, format: 'currency' },
    ];
  }, [plasticSurgeryRevenue]);

  const plasticSurgeryProfitabilitySummaryTiles = useMemo((): ReportSummaryTile[] => {
    const t = plasticSurgeryProfitability?.totals;
    if (!t) return [];
    return [
      { label: 'Patients', value: plasticSurgeryProfitability?.rows?.length ?? 0, format: 'integer' },
      { label: 'Total revenue', value: t.totalRevenue, format: 'currency' },
      { label: 'Total expenses', value: t.totalExpense, format: 'currency' },
      { label: 'Net profit', value: t.profit, format: 'currency' },
    ];
  }, [plasticSurgeryProfitability]);

  const patientBillingSummaryTiles = useMemo((): ReportSummaryTile[] => {
    const patients = patientBillingDetail?.patients ?? [];
    if (!patients.length) return [];
    const sum = (key: 'opd' | 'lab' | 'pharma' | 'additionalServices' | 'ipd' | 'dc' | 'icu' | 'total') =>
      patients.reduce((s, p) => s + Number(p[key] ?? 0), 0);
    return [
      { label: 'Patients', value: patients.length, format: 'integer' },
      { label: 'OPD revenue', value: sum('opd'), format: 'currency' },
      { label: 'Lab revenue', value: sum('lab'), format: 'currency' },
      { label: 'Pharma revenue', value: sum('pharma'), format: 'currency' },
      { label: 'Additional Services', value: sum('additionalServices'), format: 'currency' },
      { label: 'IPD revenue', value: sum('ipd'), format: 'currency' },
      { label: 'Grand total', value: sum('total'), format: 'currency' },
    ];
  }, [patientBillingDetail?.patients]);

  const pharmaRevenueSummaryTiles = useMemo((): ReportSummaryTile[] => {
    const rows = pharmaRevenueLines?.rows ?? [];
    if (!rows.length && !pharmaRevenueLines?.total) return [];
    const billLines = rows.filter((r) => r.source === 'BILLED').length;
    const ipdLines = rows.filter((r) => r.source === 'IPD_PHARMA_COLLECTION').length;
    return [
      { label: 'Total pharma revenue', value: Number(pharmaRevenueLines?.total ?? 0), format: 'currency' },
      { label: 'Line count', value: rows.length, format: 'integer' },
      { label: 'Bill payments', value: billLines, format: 'integer' },
      { label: 'IPD collections', value: ipdLines, format: 'integer' },
    ];
  }, [pharmaRevenueLines]);

  const pharmaConsumptionSummaryTiles = useMemo((): ReportSummaryTile[] => {
    const totals = pharmaConsumptionByDrug?.totals;
    if (!totals) return [];
    return [
      { label: 'Total qty consumed', value: totals.totalQty, format: 'decimal' },
      { label: 'Cost value', value: totals.costValue, format: 'currency' },
      { label: 'MRP value', value: totals.mrpValue, format: 'currency' },
      { label: 'OPD qty', value: totals.opdQty, format: 'decimal' },
      { label: 'IPD qty', value: totals.ipdQty, format: 'decimal' },
      { label: 'OT qty', value: totals.otQty, format: 'decimal' },
    ];
  }, [pharmaConsumptionByDrug]);

  const labBillingSummaryTiles = useMemo((): ReportSummaryTile[] => {
    const rows = labBillingLines?.rows ?? [];
    if (!rows.length && !labBillingLines?.total) return [];
    const opdLines = rows.filter((r) => r.source === 'OPD_BILL').length;
    const dcLines = rows.filter((r) => r.source === 'DAY_CARE_BILL').length;
    const ipdLines = rows.filter((r) => r.source === 'IPD_BILL').length;
    return [
      { label: 'Total lab billing', value: Number(labBillingLines?.total ?? 0), format: 'currency' },
      { label: 'Test lines', value: rows.length, format: 'integer' },
      { label: 'OPD bills', value: opdLines, format: 'integer' },
      { label: 'Day care bills', value: dcLines, format: 'integer' },
      { label: 'IPD bills', value: ipdLines, format: 'integer' },
    ];
  }, [labBillingLines]);

  const gstFilingSummaryTiles = useMemo((): ReportSummaryTile[] => {
    const s = gstFilingReport?.summary;
    if (!s?.lineCount) return [];
    return [
      { label: 'Total GST due', value: s.totalGst, format: 'currency' },
      { label: 'Taxable value', value: s.totalTaxableValue, format: 'currency' },
      { label: 'CGST', value: s.totalCgst, format: 'currency' },
      { label: 'SGST', value: s.totalSgst, format: 'currency' },
      { label: 'GST lines', value: s.lineCount, format: 'integer' },
    ];
  }, [gstFilingReport?.summary]);

  const additionalServicesSummaryTiles = useMemo((): ReportSummaryTile[] => {
    if (!additionalServicesBilling?.rows?.length && !additionalServicesBilling?.total) return [];
    const collected = Number(additionalServicesBilling?.totalCollected ?? 0);
    return [
      { label: 'Collected (revenue)', value: collected, format: 'currency' },
      { label: 'Total billed (packages)', value: Number(additionalServicesBilling?.total ?? 0), format: 'currency' },
      { label: 'Bill count', value: Number(additionalServicesBilling?.billCount ?? 0), format: 'integer' },
      { label: 'Service lines', value: additionalServicesBilling?.rows?.length ?? 0, format: 'integer' },
    ];
  }, [additionalServicesBilling]);

  const opdPaymentSummaryTiles = useMemo((): ReportSummaryTile[] => {
    const s = opdPaymentLinesData?.summary;
    if (!s) return [];
    return [
      { label: 'Total OPD count', value: s.totalOpdCount, format: 'integer' },
      { label: 'Total OPD revenue', value: s.totalOpdRevenue, format: 'currency' },
      { label: 'Total lab revenue', value: s.totalLabRevenue, format: 'currency' },
      { label: 'Total pharma revenue', value: s.totalPharmaRevenue, format: 'currency' },
      { label: 'Patient-days', value: s.totalPatientDays, format: 'integer' },
      { label: 'Grand total', value: s.totalRevenue, format: 'currency' },
    ];
  }, [opdPaymentLinesData?.summary]);

  const appointmentsSummaryTiles = useMemo((): ReportSummaryTile[] => {
    if (!kpis) return [];
    return [
      { label: 'Total appointments', value: kpis.totalAppointments ?? 0, format: 'integer' },
      { label: 'Completed', value: kpis.totalCompletedAppointments ?? 0, format: 'integer' },
      { label: 'Cancelled', value: kpis.totalCancelledAppointments ?? 0, format: 'integer' },
      { label: 'Follow-ups', value: kpis.totalFollowUps ?? 0, format: 'integer' },
    ];
  }, [kpis]);

  const labSummaryTiles = useMemo((): ReportSummaryTile[] => {
    if (!kpis) return [];
    const completed = (kpis.totalLabTests ?? 0) - (kpis.pendingLabTests ?? 0);
    return [
      { label: 'Total lab tests', value: kpis.totalLabTests ?? 0, format: 'integer' },
      { label: 'Pending', value: kpis.pendingLabTests ?? 0, format: 'integer' },
      { label: 'Completed', value: completed, format: 'integer' },
    ];
  }, [kpis]);

  const dayCarePaymentSummaryTiles = useMemo((): ReportSummaryTile[] => {
    const s = dayCarePaymentLinesData?.summary;
    if (!s) return [];
    return [
      { label: 'DC visit-days', value: s.totalOpdCount, format: 'integer' },
      { label: 'DC revenue', value: s.totalOpdRevenue, format: 'currency' },
      { label: 'Lab revenue', value: s.totalLabRevenue, format: 'currency' },
      { label: 'Pharma revenue', value: s.totalPharmaRevenue, format: 'currency' },
      { label: 'Patient-days', value: s.totalPatientDays, format: 'integer' },
      { label: 'Grand total', value: s.totalRevenue, format: 'currency' },
    ];
  }, [dayCarePaymentLinesData?.summary]);

  const billWiseSummaryTiles = useMemo((): ReportSummaryTile[] => {
    const totals = billWiseReport?.totals;
    if (!totals) return [];
    return [
      { label: 'Bills', value: totals.count, format: 'integer' },
      { label: 'Net amount', value: totals.netAmount, format: 'currency' },
      { label: 'Paid amount', value: totals.paidAmount, format: 'currency' },
    ];
  }, [billWiseReport?.totals]);

  const downloadBlob = (blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    setTimeout(() => {
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }, 100);
  };

  const handleDownloadRevenueExcel = async () => {
    if (!fromIso || !toIso) return;
    const resp = await businessReportsApi.revenueBreakdownExport({ from: fromIso, to: toIso, bucket, hospitalId: reportHospitalId });
    downloadBlob(new Blob([resp.data], { type: 'text/csv' }), `Revenue-Breakdown-${bucket}.csv`);
  };

  const handleDownloadPatientRevenueExcel = async () => {
    if (!fromIso || !toIso) return;
    const resp = await businessReportsApi.patientRevenueAllExport({ from: fromIso, to: toIso, hospitalId: reportHospitalId });
    const fromStr = fromIso.slice(0, 10);
    const toStr = toIso.slice(0, 10);
    downloadBlob(new Blob([resp.data], { type: 'text/csv' }), `Patient-Revenue-Report-${fromStr}-to-${toStr}.csv`);
  };

  const handleDownloadPatientBillingDetailCsv = async () => {
    if (!fromIso || !toIso) return;
    const resp = await businessReportsApi.patientBillingDetailExport({ from: fromIso, to: toIso, type: billingDetailType, hospitalId: reportHospitalId });
    const fromStr = fromIso.slice(0, 10);
    const toStr = toIso.slice(0, 10);
    downloadBlob(new Blob([resp.data], { type: 'text/csv' }), `Patient-Billing-Detail-${fromStr}-to-${toStr}-${billingDetailType}.csv`);
  };

  const handleDownloadPatientBillingItemsCsv = async () => {
    if (!fromIso || !toIso) return;
    const resp = await businessReportsApi.patientBillingDetailItemsExport({ from: fromIso, to: toIso, type: billingDetailType, hospitalId: reportHospitalId });
    const fromStr = fromIso.slice(0, 10);
    const toStr = toIso.slice(0, 10);
    downloadBlob(new Blob([resp.data], { type: 'text/csv' }), `Patient-Billing-Items-${fromStr}-to-${toStr}-${billingDetailType}.csv`);
  };

  const handleDownloadIpdProfitabilityCsv = async () => {
    if (!fromIso || !toIso) return;
    const resp = await businessReportsApi.ipdDischargedProfitabilityExport({ from: fromIso, to: toIso, hospitalId: reportHospitalId });
    const fromStr = fromIso.slice(0, 10);
    const toStr = toIso.slice(0, 10);
    downloadBlob(new Blob([resp.data], { type: 'text/csv' }), `IPD-Profitability-${fromStr}-to-${toStr}.csv`);
  };

  const handleDownloadPlasticSurgeryRevenueCsv = async () => {
    if (!fromIso || !toIso) return;
    const resp = await businessReportsApi.plasticSurgeryRevenueExport({ from: fromIso, to: toIso, hospitalId: reportHospitalId });
    const fromStr = fromIso.slice(0, 10);
    const toStr = toIso.slice(0, 10);
    downloadBlob(new Blob([resp.data], { type: 'text/csv;charset=utf-8' }), `Plastic-Surgery-Revenue-${fromStr}-to-${toStr}.csv`);
  };

  const handleDownloadPlasticSurgeryProfitabilityCsv = async () => {
    if (!fromIso || !toIso) return;
    const resp = await businessReportsApi.plasticSurgeryProfitabilityExport({ from: fromIso, to: toIso, hospitalId: reportHospitalId });
    const fromStr = fromIso.slice(0, 10);
    const toStr = toIso.slice(0, 10);
    downloadBlob(new Blob([resp.data], { type: 'text/csv;charset=utf-8' }), `Plastic-Surgery-Profitability-${fromStr}-to-${toStr}.csv`);
  };

  const handleDownloadExpenseLinesCsv = async () => {
    if (!fromIso || !toIso) return;
    const resp = await businessReportsApi.expensesReportExport({
      from: fromIso,
      to: toIso,
      hospitalId: reportHospitalId,
      ...(expenseServiceMonth.trim() ? { serviceMonth: expenseServiceMonth.trim() } : {}),
    });
    const fromStr = fromIso.slice(0, 10);
    const toStr = toIso.slice(0, 10);
    const smSuffix = expenseServiceMonth.trim() ? `-service-${expenseServiceMonth.trim()}` : '';
    downloadBlob(new Blob([resp.data], { type: 'text/csv' }), `Expense-Lines-${fromStr}-to-${toStr}${smSuffix}.csv`);
  };

  const handleDownloadMonthlyExpenseCsv = async () => {
    if (!monthlyExpenseServiceMonth.trim()) return;
    const resp = await businessReportsApi.monthlyExpensesByServiceMonthExport({
      serviceMonth: monthlyExpenseServiceMonth.trim(),
      hospitalId: reportHospitalId,
    });
    downloadBlob(
      new Blob([resp.data], { type: 'text/csv' }),
      `Monthly-Expenses-Service-${monthlyExpenseServiceMonth.trim()}.csv`
    );
  };

  const handleDownloadPnlCsv = async () => {
    const resp = await businessReportsApi.hospitalPnlExport({
      mode: pnlMode,
      month: pnlMode === 'MTD' ? pnlMonth : undefined,
      fy: pnlMode === 'YTD' || pnlMode === 'MOM' ? pnlFyStartYear : undefined,
      hospitalId: reportHospitalId,
    });
    downloadBlob(
      new Blob([resp.data], { type: 'text/csv;charset=utf-8' }),
      `PnL-${pnlMode}-${
        pnlMode === 'MTD' ? pnlMonth : `FY${pnlFyStartYear}`
      }.csv`
    );
  };

  const handleDownloadCashFlowCsv = async () => {
    await downloadCashFlowCsv({
      mode: pnlMode,
      month: pnlMode === 'MTD' ? pnlMonth : undefined,
      fy: pnlMode === 'YTD' || pnlMode === 'MOM' ? pnlFyStartYear : undefined,
      hospitalId: reportHospitalId,
    });
  };

  const handleDownloadOpdPaymentLinesCsv = async () => {
    if (!fromIso || !toIso) return;
    const resp = await businessReportsApi.opdPaymentLinesExport({
      from: fromIso,
      to: toIso,
      hospitalId: reportHospitalId ?? undefined,
    });
    const fromStr = fromIso.slice(0, 10);
    const toStr = toIso.slice(0, 10);
    downloadBlob(new Blob([resp.data], { type: 'text/csv;charset=utf-8' }), `OPD-payment-lines-${fromStr}-to-${toStr}.csv`);
  };

  const handleDownloadDayCarePaymentLinesCsv = async () => {
    if (!fromIso || !toIso) return;
    const resp = await businessReportsApi.dayCarePaymentLinesExport({
      from: fromIso,
      to: toIso,
      hospitalId: reportHospitalId ?? undefined,
    });
    const fromStr = fromIso.slice(0, 10);
    const toStr = toIso.slice(0, 10);
    downloadBlob(new Blob([resp.data], { type: 'text/csv;charset=utf-8' }), `DayCare-payment-lines-${fromStr}-to-${toStr}.csv`);
  };

  const handleDownloadSalesAttributionCsv = async () => {
    if (!fromIso || !toIso) return;
    const resp = await businessReportsApi.salesAttributionReportExport({
      from: fromIso,
      to: toIso,
      hospitalId: reportHospitalId ?? undefined,
    });
    const fromStr = fromIso.slice(0, 10);
    const toStr = toIso.slice(0, 10);
    downloadBlob(new Blob([resp.data], { type: 'text/csv;charset=utf-8' }), `Sales-attribution-${fromStr}-to-${toStr}.csv`);
  };

  const handleDownloadPharmaRevenueLinesCsv = async () => {
    if (!fromIso || !toIso) return;
    const resp = await businessReportsApi.pharmaRevenueLinesExport({
      from: fromIso,
      to: toIso,
      hospitalId: reportHospitalId ?? undefined,
    });
    const fromStr = fromIso.slice(0, 10);
    const toStr = toIso.slice(0, 10);
    downloadBlob(new Blob([resp.data], { type: 'text/csv;charset=utf-8' }), `Pharma-revenue-lines-${fromStr}-to-${toStr}.csv`);
  };

  const handleDownloadPharmaConsumptionCsv = async () => {
    if (!fromIso || !toIso) return;
    const resp = await businessReportsApi.pharmaConsumptionByDrugExport({
      from: fromIso,
      to: toIso,
      hospitalId: reportHospitalId ?? undefined,
    });
    const fromStr = fromIso.slice(0, 10);
    const toStr = toIso.slice(0, 10);
    downloadBlob(new Blob([resp.data], { type: 'text/csv;charset=utf-8' }), `Pharma-consumption-by-drug-${fromStr}-to-${toStr}.csv`);
  };

  const handleDownloadLabBillingLinesCsv = async () => {
    if (!fromIso || !toIso) return;
    const resp = await businessReportsApi.labBillingLinesExport({
      from: fromIso,
      to: toIso,
      hospitalId: reportHospitalId ?? undefined,
    });
    const fromStr = fromIso.slice(0, 10);
    const toStr = toIso.slice(0, 10);
    downloadBlob(new Blob([resp.data], { type: 'text/csv;charset=utf-8' }), `Lab-billing-lines-${fromStr}-to-${toStr}.csv`);
  };

  const handleDownloadGstFilingCsv = async () => {
    if (!fromIso || !toIso) return;
    const resp = await businessReportsApi.gstFilingLinesExport({
      from: fromIso,
      to: toIso,
      hospitalId: reportHospitalId ?? undefined,
    });
    const fromStr = fromIso.slice(0, 10);
    const toStr = toIso.slice(0, 10);
    downloadBlob(new Blob([resp.data], { type: 'text/csv;charset=utf-8' }), `GST-filing-${fromStr}-to-${toStr}.csv`);
  };

  const handleDownloadAdditionalServicesBillingCsv = async () => {
    if (!fromIso || !toIso) return;
    const resp = await businessReportsApi.additionalServicesBillingExport({
      from: fromIso,
      to: toIso,
      hospitalId: reportHospitalId ?? undefined,
    });
    const fromStr = fromIso.slice(0, 10);
    const toStr = toIso.slice(0, 10);
    downloadBlob(
      new Blob([resp.data], { type: 'text/csv;charset=utf-8' }),
      `Additional-services-billing-${fromStr}-to-${toStr}.csv`
    );
  };

  const handleDownloadBillWiseCsv = async () => {
    if (!fromIso || !toIso) return;
    const resp = await businessReportsApi.billWiseReportExport({
      from: fromIso,
      to: toIso,
      category: billWiseCategory,
      hospitalId: reportHospitalId,
    });
    const fromStr = fromIso.slice(0, 10);
    const toStr = toIso.slice(0, 10);
    const cat = billWiseCategory === 'All' ? 'all' : billWiseCategory.replace(/\s+/g, '-').toLowerCase();
    downloadBlob(new Blob([resp.data], { type: 'text/csv;charset=utf-8' }), `Bill-wise-report-${cat}-${fromStr}-to-${toStr}.csv`);
  };

  const handleDownloadPatientMasterCsv = async () => {
    if (patientMasterScope !== 'all' && (!fromIso || !toIso)) return;
    const resp = await businessReportsApi.patientMasterReportExport({
      scope: patientMasterScope,
      ...(fromIso && toIso ? { from: fromIso, to: toIso } : {}),
      hospitalId: reportHospitalId,
    });
    const fromStr = fromIso?.slice(0, 10) ?? 'all';
    const toStr = toIso?.slice(0, 10) ?? 'time';
    downloadBlob(
      new Blob([resp.data], { type: 'text/csv;charset=utf-8' }),
      `Patient-Master-${patientMasterScope}-${fromStr}-to-${toStr}.csv`
    );
  };

  const patientMasterSummaryTiles = useMemo((): ReportSummaryTile[] => {
    const s = patientMasterData?.summary;
    if (!s) return [];
    return [
      { label: 'Patients', value: s.totalPatients, format: 'integer' },
      { label: 'Total visits', value: s.totalVisits, format: 'integer' },
      { label: 'Total billing', value: s.totalBilling, format: 'currency' },
    ];
  }, [patientMasterData?.summary]);

  const revenueChart = useMemo(() => {
    if (!revenueBreakdown?.series) return null;
    const labels = revenueBreakdown.series.map((s: any) =>
      bucket === 'month' ? format(new Date(s.bucket), 'MMM yyyy') : format(new Date(s.bucket), 'dd MMM')
    );
    return {
      labels,
      datasets: [
        { label: 'OPD', data: revenueBreakdown.series.map((s: any) => s.opd), borderColor: '#2563EB', backgroundColor: 'rgba(37,99,235,0.2)' },
        { label: 'Lab', data: revenueBreakdown.series.map((s: any) => s.lab), borderColor: '#F97316', backgroundColor: 'rgba(249,115,22,0.2)' },
        { label: 'Pharma', data: revenueBreakdown.series.map((s: any) => s.pharma), borderColor: '#16A34A', backgroundColor: 'rgba(22,163,74,0.2)' },
        {
          label: 'Additional Services',
          data: revenueBreakdown.series.map((s: any) => s.additionalServices ?? 0),
          borderColor: '#0D9488',
          backgroundColor: 'rgba(13,148,136,0.2)',
        },
        { label: 'IPD', data: revenueBreakdown.series.map((s: any) => s.ipd), borderColor: '#7C3AED', backgroundColor: 'rgba(124,58,237,0.2)' },
        { label: 'DC', data: revenueBreakdown.series.map((s: any) => s.dc ?? 0), borderColor: '#EAB308', backgroundColor: 'rgba(234,179,8,0.2)' },
      ],
    };
  }, [revenueBreakdown, bucket]);

  const newPatientsChart = useMemo(() => {
    if (!newPatients?.series) return null;
    const labels = newPatients.series.map((s: { bucket: string; count: number }) =>
      formatReportBucketLabel(s.bucket, bucket)
    );
    return {
      labels,
      datasets: [
        { label: 'New patients', data: newPatients.series.map((s: any) => s.count), backgroundColor: 'rgba(37,99,235,0.6)' },
      ],
    };
  }, [newPatients, bucket]);

  const patientSourcesChart = useMemo(() => {
    if (!patientSources?.bySource?.length) return null;
    const labels = patientSources.bySource.map((s: { source: string | null; count: number }) =>
      formatPatientSourceLabel(s.source)
    );
    const data = patientSources.bySource.map((s: { source: string | null; count: number }) => s.count);
    const colors = ['#2563EB', '#F97316', '#16A34A', '#7C3AED', '#DC2626', '#0EA5E9', '#A855F7', '#F59E0B', '#14B8A6', '#64748B'];
    return {
      labels,
      datasets: [{ data, backgroundColor: labels.map((_, i) => colors[i % colors.length]) }],
    };
  }, [patientSources]);

  const handlePrint = () => {
    window.print();
  };

  if (isInvestor && investorReportKeys.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-[40vh] px-6">
        <div className="text-center max-w-md">
          <p className="text-red-600 mb-2 font-medium">No reports assigned</p>
          <p className="text-sm text-artis-slate">
            Ask your hospital admin to enable Business Reports access for your investor account.
          </p>
        </div>
      </div>
    );
  }

  if (
    isLoading &&
    reportUsesKpis &&
    investorCanFetchKpis &&
    reportType !== 'pnl' &&
    reportType !== 'cash_flow' &&
    reportType !== 'monthly_expenses' &&
    reportType !== 'monthly_performance'
  ) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="flex flex-col items-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-artis-gold" />
          <p className="text-artis-slate">Loading report data…</p>
        </div>
      </div>
    );
  }

  if (error && reportUsesKpis && investorCanFetchKpis) {
    return (
      <div className="flex items-center justify-center min-h-[50vh] px-6">
        <div className="text-center">
          <p className="text-red-600 mb-4 font-medium">Failed to load report data</p>
          <Button
            onClick={() => window.location.reload()}
            variant="outline"
            className={cn(isInvestor && investorActionBtnClass)}
          >
            Retry
          </Button>
        </div>
      </div>
    );
  }

  const completionRate = kpis?.totalAppointments
    ? Math.round((kpis.totalCompletedAppointments / kpis.totalAppointments) * 100)
    : 0;
  const labCompletionRate = kpis?.totalLabTests
    ? Math.round(((kpis.totalLabTests - kpis.pendingLabTests) / kpis.totalLabTests) * 100)
    : 0;

  const reportTitle = {
    summary: 'Summary Report',
    revenue: 'Revenue Report',
    appointments: 'Appointments Report',
    lab: 'Lab Operations Report',
    revenue_breakdown: 'Revenue Breakdown (OPD/IPD/Lab/Pharma/Additional Services/DC)',
    pharma_revenue: 'Pharma Revenue (Detailed)',
    pharma_consumption: 'Medicine Consumption (OPD / IPD / OT)',
    lab_billing: 'Lab Billing (Detailed)',
    gst_filing: 'GST Filing Report',
    additional_services: 'Additional Services Report',
    expenses: 'Expense Report',
    monthly_expenses: 'Monthly Expenses (by service month)',
    monthly_performance: 'Monthly Business Performance',
    pnl: 'P&L Statement (MTD / YTD / MoM)',
    cash_flow: 'Cash Flow Statement (MTD / YTD / MoM)',
    patients: 'Patient Analytics',
    patient_billing: 'Patient-wise Billing Detail',
    ipd_profitability: 'IPD Profitability (Discharged)',
    plastic_surgery_revenue: 'Plastic Surgery Revenue',
    plastic_surgery_profitability: 'Plastic Surgery Profitability (Patient-wise)',
    opd_payment_lines: 'OPD Payments (Line-wise)',
    daycare_payment_lines: 'Day Care Payments (Line-wise)',
    sales_attribution: 'Sales Report',
    bill_wise: 'Bill-wise Report (All modules)',
    patient_master: 'Patient Master',
  }[reportType];

  return (
    <div className={cn('space-y-5 print:p-4', isInvestor ? 'p-4 sm:p-6 font-artis-sans' : 'p-6 space-y-6')}>
      {/* Header & Filters */}
      <div
        className={cn(
          'flex flex-col gap-4 print:mb-4',
          isInvestor
            ? 'rounded-xl border border-artis-brown/10 bg-artis-cream/70 p-4 sm:p-5'
            : 'sm:flex-row sm:items-center sm:justify-between'
        )}
      >
        {!isInvestor && (
        <div>
          <h1 className="thms-page-title flex items-center gap-2">
            <FileText className="w-8 h-8 text-blue-600" />
            {salesOnly ? 'Sales Report' : summaryOnly ? 'Summary Report' : 'Business Reports'}
          </h1>
          <p className="thms-page-subtitle">
            {salesOnly
              ? 'Review CRM sales attribution and missed IPD conversions'
              : summaryOnly
                ? 'Revenue, appointments, lab, and dashboard KPIs for your hospital'
                : 'Generate and view hospital reports by time period'}
          </p>
        </div>
        )}
        {isInvestor && (
          <div className="mb-1">
            <p className="font-artis-serif text-base font-semibold text-artis-brown-dark">Report filters</p>
            <p className="text-xs text-artis-slate">Choose hospital scope, period, and report type</p>
          </div>
        )}
        <div className={cn('flex flex-wrap items-center gap-3', isInvestor && 'sm:justify-start')}>
          {(showHospitalFilter || reportType === 'pnl' || reportType === 'cash_flow') &&
          ((reportType === 'pnl' && canViewPnl) || (reportType === 'cash_flow' && canViewCashFlow)) ? (
            <div className="flex items-end gap-2">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">
                  {reportType === 'cash_flow' ? 'Cash Flow scope' : 'P&L scope'}
                </Label>
                <Select
                  value={
                    isSuperAdmin
                      ? selectedHospitalId || 'all'
                      : selectedHospitalId || 'all'
                  }
                  onValueChange={(v) =>
                    setSelectedHospitalId(v === 'all' && isSuperAdmin ? '' : v)
                  }
                >
                  <SelectTrigger className="w-[280px]">
                    <SelectValue placeholder="Hospital or consolidated" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Consolidated (all hospitals)</SelectItem>
                    {isSuperAdmin
                      ? (hospitals || []).map((h) => (
                          <SelectItem key={h.id} value={h.id}>
                            {h.name} (hospital-wise)
                          </SelectItem>
                        ))
                      : chainHospitals.map((h) => (
                          <SelectItem key={h.id} value={h.id}>
                            {h.name} (hospital-wise)
                          </SelectItem>
                        ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            showHospitalFilter && (
            <Select
              value={
                isSuperAdmin
                  ? selectedHospitalId || 'all'
                  : selectedHospitalId || activeHospitalId || 'all'
              }
              onValueChange={(v) => setSelectedHospitalId(v === 'all' && isSuperAdmin ? '' : v)}
            >
              <SelectTrigger className="w-[240px]">
                <SelectValue placeholder="Hospital" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">
                  {isChainAdmin ? 'Consolidated (all hospitals)' : 'All hospitals'}
                </SelectItem>
                {isSuperAdmin
                  ? (hospitals || []).map((h) => (
                      <SelectItem key={h.id} value={h.id}>
                        {h.name}
                      </SelectItem>
                    ))
                  : chainHospitals.map((h) => (
                      <SelectItem key={h.id} value={h.id}>
                        {h.name}
                      </SelectItem>
                    ))}
              </SelectContent>
            </Select>
            )
          )}
          {reportType !== 'pnl' && reportType !== 'cash_flow' && (
          <TimeIntervalFilter
            onTimeIntervalChange={handleTimeIntervalChange}
            selectedInterval={selectedInterval}
            onIntervalChange={handleIntervalChange}
            selectedMonth={selectedMonth}
            onMonthChange={handleMonthChange}
            selectedYear={selectedYear}
            onYearChange={handleYearChange}
          />
          )}
          {(reportType === 'pnl' || reportType === 'cash_flow') && (
            <div className="flex flex-wrap items-end gap-2">
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Period</Label>
                <Select value={pnlMode} onValueChange={(v) => setPnlMode(v as 'MTD' | 'YTD' | 'MOM')}>
                  <SelectTrigger className="w-[140px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="MTD">MTD</SelectItem>
                    <SelectItem value="YTD">YTD (FY)</SelectItem>
                    <SelectItem value="MOM">MoM (FY)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {pnlMode === 'MTD' ? (
                <div className="space-y-1">
                  <Label htmlFor="pnl-month" className="text-xs text-muted-foreground">
                    Month
                  </Label>
                  <Input
                    id="pnl-month"
                    type="month"
                    value={pnlMonth}
                    onChange={(e) => setPnlMonth(e.target.value)}
                    className="w-[180px]"
                  />
                </div>
              ) : (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Financial year</Label>
                  <Select
                    value={String(pnlFyStartYear)}
                    onValueChange={(v) => setPnlFyStartYear(Number(v))}
                  >
                    <SelectTrigger className="w-[180px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[0, 1, 2, 3, 4].map((offset) => {
                        const now = new Date();
                        const curFy = now.getMonth() >= 3 ? now.getFullYear() : now.getFullYear() - 1;
                        const y = curFy - offset;
                        return (
                          <SelectItem key={y} value={String(y)}>
                            FY {y}-{String(y + 1).slice(-2)}
                          </SelectItem>
                        );
                      })}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <Button
                onClick={reportType === 'cash_flow' ? handleDownloadCashFlowCsv : handleDownloadPnlCsv}
                variant="outline"
                className={cn('print:hidden', investorActionBtnClass)}
              >
                <Download className="w-4 h-4 mr-2" />
                Download CSV
              </Button>
            </div>
          )}
          {reportType === 'monthly_expenses' && (
            <div className="flex items-end gap-2">
              <div className="space-y-1">
                <Label htmlFor="monthly-expense-service-month" className="text-xs text-muted-foreground">
                  Service month
                </Label>
                <Input
                  id="monthly-expense-service-month"
                  type="month"
                  value={monthlyExpenseServiceMonth}
                  onChange={(e) => setMonthlyExpenseServiceMonth(e.target.value)}
                  className="w-[180px]"
                />
              </div>
            </div>
          )}
          {reportType === 'monthly_performance' && (
            <div className="flex items-end gap-2">
              <div className="space-y-1">
                <Label htmlFor="performance-month" className="text-xs text-muted-foreground">
                  Report month
                </Label>
                <Input
                  id="performance-month"
                  type="month"
                  value={performanceMonth}
                  onChange={(e) => setPerformanceMonth(e.target.value)}
                  className="w-[180px]"
                />
              </div>
            </div>
          )}
          {!salesOnly && !summaryOnly && (
            <Select
              value={reportType}
              onValueChange={(v) => {
                const next = v as ReportType;
                setReportType(next);
                if (next === 'pnl' && canViewPnl) {
                  setSelectedHospitalId(defaultFinancialHospitalId);
                }
                if (next === 'cash_flow' && canViewCashFlow) {
                  setSelectedHospitalId(defaultFinancialHospitalId);
                }
              }}
            >
              <SelectTrigger className="w-[260px]">
                <SelectValue placeholder="Report type" />
              </SelectTrigger>
              <SelectContent>
                {canAccessReportType('summary') && <SelectItem value="summary">Summary Report</SelectItem>}
                {canAccessReportType('revenue') && <SelectItem value="revenue">Revenue Report</SelectItem>}
                {canAccessReportType('revenue_breakdown') && <SelectItem value="revenue_breakdown">Revenue Breakdown</SelectItem>}
                {canAccessReportType('pharma_revenue') && <SelectItem value="pharma_revenue">Pharma revenue (detailed)</SelectItem>}
                {canAccessReportType('pharma_consumption') && (
                  <SelectItem value="pharma_consumption">Medicine consumption (OPD/IPD/OT)</SelectItem>
                )}
                {canAccessReportType('lab_billing') && <SelectItem value="lab_billing">Lab billing (detailed)</SelectItem>}
                {canAccessReportType('gst_filing') && <SelectItem value="gst_filing">GST filing report</SelectItem>}
                {canAccessReportType('additional_services') && <SelectItem value="additional_services">Additional Services Report</SelectItem>}
                {canAccessReportType('appointments') && <SelectItem value="appointments">Appointments Report</SelectItem>}
                {canAccessReportType('lab') && <SelectItem value="lab">Lab Operations Report</SelectItem>}
                {canAccessReportType('expenses') && <SelectItem value="expenses">Expense Report</SelectItem>}
                {canAccessReportType('monthly_expenses') && <SelectItem value="monthly_expenses">Monthly expenses (service month)</SelectItem>}
                {canAccessReportType('monthly_performance') && (
                  <SelectItem value="monthly_performance">Monthly Business Performance</SelectItem>
                )}
                {canViewPnl && (
                  <SelectItem value="pnl">P&amp;L Statement (MTD / YTD / MoM)</SelectItem>
                )}
                {canViewCashFlow && (
                  <SelectItem value="cash_flow">Cash Flow Statement (MTD / YTD / MoM)</SelectItem>
                )}
                {canAccessReportType('patients') && <SelectItem value="patients">Patient Analytics</SelectItem>}
                {canAccessReportType('patient_master') && <SelectItem value="patient_master">Patient Master</SelectItem>}
                {canAccessReportType('patient_billing') && <SelectItem value="patient_billing">Patient-wise Billing Detail</SelectItem>}
                {canAccessReportType('bill_wise') && <SelectItem value="bill_wise">Bill-wise Report (All modules)</SelectItem>}
                {canAccessReportType('ipd_profitability') && <SelectItem value="ipd_profitability">IPD Profitability (Discharged)</SelectItem>}
                {canAccessReportType('plastic_surgery_revenue') && <SelectItem value="plastic_surgery_revenue">Plastic Surgery Revenue</SelectItem>}
                {canAccessReportType('plastic_surgery_profitability') && <SelectItem value="plastic_surgery_profitability">Plastic Surgery Profitability (Patient-wise)</SelectItem>}
                {canAccessReportType('opd_payment_lines') && <SelectItem value="opd_payment_lines">OPD Payments (line-wise CSV)</SelectItem>}
                {canAccessReportType('daycare_payment_lines') && <SelectItem value="daycare_payment_lines">Day Care Payments (line-wise CSV)</SelectItem>}
                {canAccessReportType('sales_attribution') && <SelectItem value="sales_attribution">Sales Report</SelectItem>}
              </SelectContent>
            </Select>
          )}

          {(reportType === 'revenue_breakdown' || reportType === 'patients') && (
            <Select value={bucket} onValueChange={(v) => setBucket(v as 'day' | 'month')}>
              <SelectTrigger className="w-[140px]">
                <SelectValue placeholder="Bucket" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="day">Daily</SelectItem>
                <SelectItem value="month">Monthly</SelectItem>
              </SelectContent>
            </Select>
          )}

          {reportType === 'patient_master' && (
            <Select value={patientMasterScope} onValueChange={(v) => setPatientMasterScope(v as PatientMasterScope)}>
              <SelectTrigger className="w-[240px]">
                <SelectValue placeholder="Patient scope" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All patients (all time)</SelectItem>
                <SelectItem value="active">Active in date range</SelectItem>
                <SelectItem value="registered">Registered in date range</SelectItem>
              </SelectContent>
            </Select>
          )}

          {reportType === 'patient_billing' && (
            <Select value={billingDetailType} onValueChange={(v) => setBillingDetailType(v as BillingReportType)}>
              <SelectTrigger className="w-[160px]">
                <SelectValue placeholder="Billing type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="All">All</SelectItem>
                <SelectItem value="OPD">OPD</SelectItem>
                <SelectItem value="IPD">IPD</SelectItem>
                <SelectItem value="DC">DC</SelectItem>
                <SelectItem value="ICU">ICU</SelectItem>
                <SelectItem value="Lab">Lab</SelectItem>
                <SelectItem value="Pharma">Pharma</SelectItem>
                <SelectItem value="AdditionalServices">Additional Services</SelectItem>
              </SelectContent>
            </Select>
          )}

          {reportType === 'patient_billing' && (
            <>
              <Select value={billingDetailView} onValueChange={(v) => setBillingDetailView(v as 'summary' | 'items')}>
                <SelectTrigger className="w-[160px]">
                  <SelectValue placeholder="View" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="summary">Summary by patient</SelectItem>
                  <SelectItem value="items">Item-wise detail</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={billingDetailView === 'items' ? handleDownloadPatientBillingItemsCsv : handleDownloadPatientBillingDetailCsv} variant="outline" className={cn('print:hidden', investorActionBtnClass)} disabled={!fromIso || !toIso}>
                <Download className="w-4 h-4 mr-2" />
                Download CSV
              </Button>
            </>
          )}

          {reportType === 'ipd_profitability' && (
            <Button onClick={handleDownloadIpdProfitabilityCsv} variant="outline" className={cn('print:hidden', investorActionBtnClass)} disabled={!fromIso || !toIso}>
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
          )}

          {reportType === 'plastic_surgery_revenue' && (
            <Button onClick={handleDownloadPlasticSurgeryRevenueCsv} variant="outline" className={cn('print:hidden', investorActionBtnClass)} disabled={!fromIso || !toIso}>
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
          )}

          {reportType === 'plastic_surgery_profitability' && (
            <Button onClick={handleDownloadPlasticSurgeryProfitabilityCsv} variant="outline" className={cn('print:hidden', investorActionBtnClass)} disabled={!fromIso || !toIso}>
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
          )}

          {reportType === 'revenue_breakdown' && (
            <Button onClick={handleDownloadRevenueExcel} variant="outline" className={cn('print:hidden', investorActionBtnClass)} disabled={!fromIso || !toIso}>
              <Download className="w-4 h-4 mr-2" />
              Download Excel
            </Button>
          )}
          {reportType === 'patients' && (
            <Button onClick={handleDownloadPatientRevenueExcel} variant="outline" className={cn('print:hidden', investorActionBtnClass)} disabled={!fromIso || !toIso}>
              <Download className="w-4 h-4 mr-2" />
              Download patient revenue (CSV)
            </Button>
          )}
          {reportType === 'monthly_expenses' && (
            <Button
              onClick={handleDownloadMonthlyExpenseCsv}
              variant="outline"
              className={cn('print:hidden', investorActionBtnClass)}
              disabled={!monthlyExpenseServiceMonth.trim()}
            >
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
          )}
          {reportType === 'expenses' && (
            <>
              <div className="flex items-end gap-2">
                <div className="space-y-1">
                  <Label htmlFor="expense-service-month" className="text-xs text-muted-foreground">
                    Service month
                  </Label>
                  <Input
                    id="expense-service-month"
                    type="month"
                    value={expenseServiceMonth}
                    onChange={(e) => setExpenseServiceMonth(e.target.value)}
                    className="w-[160px]"
                  />
                </div>
                {expenseServiceMonth && (
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mb-0.5"
                    onClick={() => setExpenseServiceMonth('')}
                  >
                    Clear
                  </Button>
                )}
              </div>
              <Button onClick={handleDownloadExpenseLinesCsv} variant="outline" className={cn('print:hidden', investorActionBtnClass)} disabled={!fromIso || !toIso}>
                <Download className="w-4 h-4 mr-2" />
                Download detailed (CSV)
              </Button>
            </>
          )}
          {reportType === 'opd_payment_lines' && (
            <Button onClick={handleDownloadOpdPaymentLinesCsv} variant="outline" className={cn('print:hidden', investorActionBtnClass)} disabled={!fromIso || !toIso}>
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
          )}
          {reportType === 'daycare_payment_lines' && (
            <Button onClick={handleDownloadDayCarePaymentLinesCsv} variant="outline" className={cn('print:hidden', investorActionBtnClass)} disabled={!fromIso || !toIso}>
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
          )}
          {reportType === 'sales_attribution' && (
            <Button onClick={handleDownloadSalesAttributionCsv} variant="outline" className={cn('print:hidden', investorActionBtnClass)} disabled={!fromIso || !toIso}>
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
          )}
          {reportType === 'pharma_revenue' && (
            <Button onClick={handleDownloadPharmaRevenueLinesCsv} variant="outline" className={cn('print:hidden', investorActionBtnClass)} disabled={!fromIso || !toIso}>
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
          )}
          {reportType === 'pharma_consumption' && (
            <Button onClick={handleDownloadPharmaConsumptionCsv} variant="outline" className={cn('print:hidden', investorActionBtnClass)} disabled={!fromIso || !toIso}>
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
          )}
          {reportType === 'lab_billing' && (
            <Button onClick={handleDownloadLabBillingLinesCsv} variant="outline" className={cn('print:hidden', investorActionBtnClass)} disabled={!fromIso || !toIso}>
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
          )}
          {reportType === 'gst_filing' && (
            <Button onClick={handleDownloadGstFilingCsv} variant="outline" className={cn('print:hidden', investorActionBtnClass)} disabled={!fromIso || !toIso}>
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
          )}
          {reportType === 'additional_services' && (
            <Button onClick={handleDownloadAdditionalServicesBillingCsv} variant="outline" className={cn('print:hidden', investorActionBtnClass)} disabled={!fromIso || !toIso}>
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
          )}

          {reportType === 'patient_master' && (
            <Button
              onClick={handleDownloadPatientMasterCsv}
              variant="outline"
              className={cn('print:hidden', investorActionBtnClass)}
              disabled={patientMasterScope !== 'all' && (!fromIso || !toIso)}
            >
              <Download className="w-4 h-4 mr-2" />
              Download CSV
            </Button>
          )}
          {reportType === 'bill_wise' && (
            <>
              <Select value={billWiseCategory} onValueChange={(v) => setBillWiseCategory(v as BillWiseCategory)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Bill category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="All">All categories</SelectItem>
                  <SelectItem value="OPD">OPD</SelectItem>
                  <SelectItem value="Lab">Lab</SelectItem>
                  <SelectItem value="Pharma">Pharma</SelectItem>
                  <SelectItem value="Day Care">Day Care</SelectItem>
                  <SelectItem value="Additional Services">Additional Services</SelectItem>
                  <SelectItem value="IPD">IPD</SelectItem>
                  <SelectItem value="ICU">ICU</SelectItem>
                  <SelectItem value="IPD Pharma">IPD Pharma (billed separately)</SelectItem>
                  <SelectItem value="Mixed">Mixed (OPD+Lab+Pharma)</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={handleDownloadBillWiseCsv} variant="outline" className={cn('print:hidden', investorActionBtnClass)} disabled={!fromIso || !toIso}>
                <Download className="w-4 h-4 mr-2" />
                Download CSV
              </Button>
            </>
          )}
          <Button onClick={handlePrint} variant="outline" className={cn('print:hidden', investorActionBtnClass)}>
            <Printer className="w-4 h-4 mr-2" />
            Print report
          </Button>
        </div>
      </div>

      {/* Date range info */}
      {kpis?.period && reportType !== 'monthly_expenses' && reportType !== 'monthly_performance' && reportType !== 'pnl' && reportType !== 'cash_flow' && (
        <div
          className={cn(
            'px-4 py-2.5 rounded-lg text-sm',
            isInvestor
              ? 'rounded-lg border border-artis-gold/25 bg-artis-sand/40 px-4 py-2.5 text-sm text-artis-brown-dark'
              : 'bg-blue-50 text-blue-800 rounded-md'
          )}
        >
          Showing data from {format(new Date(kpis.period.start), 'PPP')} to{' '}
          {format(new Date(kpis.period.end), 'PPP')} — {reportTitle}
        </div>
      )}
      {!kpis?.period &&
        startDate &&
        endDate &&
        reportType !== 'monthly_expenses' &&
        reportType !== 'monthly_performance' &&
        reportType !== 'pnl' &&
        reportType !== 'cash_flow' &&
        !reportUsesKpis && (
          <div
            className={cn(
              'px-4 py-2.5 rounded-lg text-sm',
              isInvestor
                ? 'rounded-lg border border-artis-gold/25 bg-artis-sand/40 px-4 py-2.5 text-sm text-artis-brown-dark'
                : 'bg-blue-50 text-blue-800 rounded-md'
            )}
          >
            Showing data from {format(startDate, 'PPP')} to {format(endDate, 'PPP')} — {reportTitle}
          </div>
        )}
      {reportType === 'pnl' && pnlReport && (
        <div className="thms-info-banner">
          {'view' in pnlReport && pnlReport.view === 'MOM'
            ? (pnlReport as HospitalPnlMomResponse).label
            : (pnlReport as HospitalPnlResponse).period?.label}
          {' — '}
          {reportHospitalId && reportHospitalId !== 'all'
            ? 'Hospital-wise P&L'
            : 'Consolidated P&L (all hospitals in scope)'}
          {pnlMode === 'MOM'
            ? ' · Month-on-month columns (Apr–Mar FY) · A42/A42C salary uses prior month when not yet posted (est.)'
            : ''}
          {' · '}
          Aesthetic = Plastic Surgery · Central expenses by service month (DRAFT–PAID) · GST /
          Gateway / Marketplace shown as ₹0
        </div>
      )}
      {reportType === 'cash_flow' && cashFlowReport && (
        <div className="bg-emerald-50 text-emerald-900 px-4 py-2 rounded-md text-sm">
          {'view' in cashFlowReport && cashFlowReport.view === 'MOM'
            ? cashFlowReport.label
            : cashFlowReport.period?.label}
          {' — '}
          {reportHospitalId && reportHospitalId !== 'all'
            ? 'Hospital-wise Cash Flow'
            : 'Consolidated Cash Flow'}
          {pnlMode === 'MOM' ? ' · Month-on-month (Apr–Mar FY)' : ''}
        </div>
      )}
      {salesOnly && startDate && endDate && (
        <div className="thms-info-banner">
          Showing data from {format(startDate, 'PPP')} to {format(endDate, 'PPP')} — Sales Report
        </div>
      )}
      {reportType === 'monthly_expenses' && monthlyExpenseServiceMonth && (
        <div className="thms-info-banner">
          Showing draft, pending, approved, and paid expenses with service month{' '}
          <strong>{formatServiceMonthLabel(monthlyExpenseServiceMonth)}</strong> (expense entry date is ignored;
          rejected excluded).
        </div>
      )}

      {/* Report content by type */}
      <div className="report-content">
        {reportType === 'expenses' && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-red-600" />
                Expense Report
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                IPD patient expenses and central expenses for the selected date range (approved/paid).
                {expenseServiceMonth.trim() ? (
                  <> Filtered to service month <strong>{expenseServiceMonth}</strong>.</>
                ) : (
                  <> Optionally filter by the service month recorded on each expense entry.</>
                )}
              </p>
              <div className="flex flex-wrap items-end gap-3 pt-2 print:hidden">
                <div className="space-y-1">
                  <Label htmlFor="expense-service-month-card" className="text-xs text-muted-foreground">
                    Service month filter
                  </Label>
                  <Input
                    id="expense-service-month-card"
                    type="month"
                    value={expenseServiceMonth}
                    onChange={(e) => setExpenseServiceMonth(e.target.value)}
                    className="w-[180px]"
                  />
                </div>
                {expenseServiceMonth && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setExpenseServiceMonth('')}>
                    Clear service month
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {!expenseReport ? (
                <div className="text-sm text-artis-slate">Loading…</div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">IPD patient expenses</CardTitle>
                      </CardHeader>
                      <CardContent className="text-xl font-semibold">
                        ₹{Number(expenseReport.totals.ipdPatient || 0).toLocaleString('en-IN')}
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Central expenses</CardTitle>
                      </CardHeader>
                      <CardContent className="text-xl font-semibold">
                        ₹{Number(expenseReport.totals.central || 0).toLocaleString('en-IN')}
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Total expenses</CardTitle>
                      </CardHeader>
                      <CardContent className="text-xl font-semibold">
                        ₹{Number(expenseReport.totals.total || 0).toLocaleString('en-IN')}
                      </CardContent>
                    </Card>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="min-w-full border text-sm rounded">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="p-2 text-left">Date</th>
                          <th className="p-2 text-right">IPD (Reception)</th>
                          <th className="p-2 text-right">IPD (Admin)</th>
                          <th className="p-2 text-right">Central (Reception)</th>
                          <th className="p-2 text-right">Central (Admin)</th>
                          <th className="p-2 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(expenseReport.series || []).map((r) => (
                          <tr key={r.bucket} className="border-t">
                            <td className="p-2">{r.bucket}</td>
                            <td className="p-2 text-right">
                              ₹{Number((r as any).ipdReception || 0).toLocaleString('en-IN')}
                            </td>
                            <td className="p-2 text-right">
                              ₹{Number((r as any).ipdAdmin || 0).toLocaleString('en-IN')}
                            </td>
                            <td className="p-2 text-right">
                              ₹{Number((r as any).centralReception || 0).toLocaleString('en-IN')}
                            </td>
                            <td className="p-2 text-right">
                              ₹{Number((r as any).centralAdmin || 0).toLocaleString('en-IN')}
                            </td>
                            <td className="p-2 text-right font-medium">
                              ₹{Number(r.total || 0).toLocaleString('en-IN')}
                            </td>
                          </tr>
                        ))}
                        {(!expenseReport.series || expenseReport.series.length === 0) && (
                          <tr className="border-t">
                            <td className="p-3 text-center text-gray-500" colSpan={6}>
                              No expenses found for selected range.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {(expenseReport.lines?.length ?? 0) > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium">Expense line items</h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full border text-sm rounded">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="p-2 text-left">Date</th>
                              <th className="p-2 text-left">Type</th>
                              <th className="p-2 text-left">Category</th>
                              <th className="p-2 text-left">Service month</th>
                              <th className="p-2 text-left">Vendor</th>
                              <th className="p-2 text-left">Entered by</th>
                              <th className="p-2 text-right">Amount</th>
                              <th className="p-2 text-left">Invoice</th>
                            </tr>
                          </thead>
                          <tbody>
                            {expenseReport.lines!.map((row, idx) => (
                              <tr key={`${row.expenseType}-${row.expenseId ?? row.expenseDate}-${idx}`} className="border-t">
                                <td className="p-2 whitespace-nowrap">
                                  {format(new Date(row.expenseDate), 'dd/MM/yyyy')}
                                </td>
                                <td className="p-2 whitespace-nowrap">
                                  {row.expenseType === 'PATIENT' ? 'IPD patient' : 'Central'}
                                </td>
                                <td className="p-2">{row.category}</td>
                                <td className="p-2 whitespace-nowrap">{row.serviceMonth || '—'}</td>
                                <td className="p-2 max-w-[200px] truncate">{row.vendorName || '—'}</td>
                                <td className="p-2 max-w-[160px] truncate">{row.createdByName || '—'}</td>
                                <td className="p-2 text-right font-medium">
                                  ₹{Number(row.amount || 0).toLocaleString('en-IN')}
                                </td>
                                <td className="p-2">
                                  {(row.proofs?.length ?? 0) > 0 || row.proofUrl ? (
                                    <div className="flex flex-col gap-0.5">
                                      {(row.proofs?.length
                                        ? row.proofs
                                        : row.proofUrl
                                          ? [{ id: 'legacy', url: row.proofUrl, fileName: row.proofFileName || 'Invoice' }]
                                          : []
                                      ).map((p) => (
                                        <a
                                          key={p.id}
                                          href={p.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-primary text-xs underline-offset-2 hover:underline truncate max-w-[140px]"
                                          title={p.fileName}
                                        >
                                          {p.fileName || 'View'}
                                        </a>
                                      ))}
                                    </div>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {reportType === 'monthly_expenses' && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-red-600" />
                Monthly Expenses (by service month)
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                All draft, pending approval, approved, and paid expenses tagged with the selected service month
                when entered — regardless of when the expense was recorded (rejected excluded). Pick the month
                users select on the expense form (e.g. June).
              </p>
            </CardHeader>
            <CardContent>
              {!monthlyExpenseReport ? (
                <div className="text-sm text-artis-slate">Loading…</div>
              ) : (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">IPD patient expenses</CardTitle>
                      </CardHeader>
                      <CardContent className="text-xl font-semibold">
                        ₹{Number(monthlyExpenseReport.totals.ipdPatient || 0).toLocaleString('en-IN')}
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Central expenses</CardTitle>
                      </CardHeader>
                      <CardContent className="text-xl font-semibold">
                        ₹{Number(monthlyExpenseReport.totals.central || 0).toLocaleString('en-IN')}
                      </CardContent>
                    </Card>
                    <Card>
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm">Total</CardTitle>
                      </CardHeader>
                      <CardContent className="text-xl font-semibold">
                        ₹{Number(monthlyExpenseReport.totals.total || 0).toLocaleString('en-IN')}
                      </CardContent>
                    </Card>
                  </div>

                  {(monthlyExpenseReport.byCategory?.length ?? 0) > 0 && (
                    <div className="overflow-x-auto">
                      <h4 className="text-sm font-medium mb-2">By category</h4>
                      <table className="min-w-full border text-sm rounded">
                        <thead className="bg-gray-50">
                          <tr>
                            <th className="p-2 text-left">Type</th>
                            <th className="p-2 text-left">Category</th>
                            <th className="p-2 text-right">Count</th>
                            <th className="p-2 text-right">Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {monthlyExpenseReport.byCategory.map((row) => (
                            <tr key={`${row.expenseType}-${row.category}`} className="border-t">
                              <td className="p-2">
                                {row.expenseType === 'PATIENT' ? 'IPD patient' : 'Central'}
                              </td>
                              <td className="p-2">{row.category}</td>
                              <td className="p-2 text-right">{row.count}</td>
                              <td className="p-2 text-right font-medium">
                                ₹{Number(row.amount || 0).toLocaleString('en-IN')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                  <div className="overflow-x-auto">
                    <h4 className="text-sm font-medium mb-2">Expense line items</h4>
                    <table className="min-w-full border text-sm rounded">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="p-2 text-left">Expense date</th>
                          <th className="p-2 text-left">Type</th>
                          <th className="p-2 text-left">Category</th>
                          <th className="p-2 text-left">Service month</th>
                          <th className="p-2 text-left">Status</th>
                          <th className="p-2 text-left">Vendor</th>
                          <th className="p-2 text-left">Entered by</th>
                          <th className="p-2 text-right">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(monthlyExpenseReport.lines || []).map((row, idx) => (
                          <tr key={`${row.expenseType}-${row.expenseDate}-${idx}`} className="border-t">
                            <td className="p-2 whitespace-nowrap">
                              {format(new Date(row.expenseDate), 'dd/MM/yyyy')}
                            </td>
                            <td className="p-2 whitespace-nowrap">
                              {row.expenseType === 'PATIENT' ? 'IPD patient' : 'Central'}
                            </td>
                            <td className="p-2">{row.category}</td>
                            <td className="p-2 whitespace-nowrap">{row.serviceMonth || '—'}</td>
                            <td className="p-2 whitespace-nowrap">{row.workflowStatus || '—'}</td>
                            <td className="p-2 max-w-[200px] truncate">{row.vendorName || '—'}</td>
                            <td className="p-2 max-w-[160px] truncate">{row.createdByName || '—'}</td>
                            <td className="p-2 text-right font-medium">
                              ₹{Number(row.amount || 0).toLocaleString('en-IN')}
                            </td>
                          </tr>
                        ))}
                        {(!monthlyExpenseReport.lines || monthlyExpenseReport.lines.length === 0) && (
                          <tr className="border-t">
                            <td className="p-3 text-center text-gray-500" colSpan={8}>
                              No expenses found for service month{' '}
                              {formatServiceMonthLabel(monthlyExpenseServiceMonth)}.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {reportType === 'monthly_performance' && (
          <>
            {monthlyPerformanceLoading ? (
              <div className="flex items-center justify-center min-h-[40vh]">
                <div className="flex flex-col items-center space-y-4">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-artis-gold" />
                  <p className="text-artis-slate">Loading monthly performance report…</p>
                </div>
              </div>
            ) : monthlyPerformanceReport ? (
              <MonthlyBusinessPerformanceReport data={monthlyPerformanceReport} />
            ) : (
              <Card>
                <CardContent className="py-10 text-center text-muted-foreground">
                  Select a report month to view performance.
                </CardContent>
              </Card>
            )}
          </>
        )}

        {reportType === 'pnl' && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-blue-600" />
                P&amp;L Statement{pnlMode === 'MOM' ? ' — Month on Month' : ''}
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                {pnlMode === 'MOM'
                  ? 'One column per month in the selected Indian FY (current FY through today). '
                  : 'Select MTD, YTD, or MoM. '}
                Aesthetic uses Plastic Surgery rules. Use <strong>P&amp;L scope</strong> for
                consolidated vs hospital-wise. Chain admin only.
              </p>
            </CardHeader>
            <CardContent>
              {loadingPnl ? (
                <div className="text-sm text-artis-slate">
                  Loading P&amp;L…
                  {pnlMode === 'MOM' ? ' MoM builds each month sequentially — may take up to a minute.' : ''}
                </div>
              ) : pnlError ? (
                <div className="text-sm text-destructive">
                  {(pnlQueryError as { response?: { data?: { message?: string } } })?.response?.data
                    ?.message ||
                    (pnlQueryError instanceof Error ? pnlQueryError.message : null) ||
                    'Failed to load P&L. Please retry or use MTD for a single month.'}
                </div>
              ) : !pnlReport ? (
                <div className="text-sm text-artis-slate">No P&amp;L data for this selection.</div>
              ) : 'view' in pnlReport && pnlReport.view === 'MOM' ? (
                (() => {
                  const mom = pnlReport as HospitalPnlMomResponse;
                  const template = mom.months[0]?.lines ?? [];
                  const pnlHospitalParam =
                    reportHospitalId === 'all' ? 'all' : reportHospitalId;
                  const pnlConsolidated =
                    reportHospitalId === 'all' || reportHospitalId == null;
                  return (
                    <div className="space-y-4">
                      <div className="overflow-x-auto">
                        <table className="min-w-full border text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="p-2 text-left sticky left-0 bg-gray-50 z-10">ID</th>
                              <th className="p-2 text-left sticky left-10 bg-gray-50 z-10 min-w-[180px]">
                                Metric
                              </th>
                              {mom.months.map((m) => (
                                <th key={m.month} className="p-2 text-right whitespace-nowrap">
                                  {m.label}
                                </th>
                              ))}
                              <th className="p-2 text-right whitespace-nowrap bg-slate-100 font-semibold">
                                FY Total
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {template.map((row, idx) => {
                              const prev = idx > 0 ? template[idx - 1] : null;
                              const sectionBreak = prev && prev.section !== row.section;
                              const emphasize = [
                                'A14',
                                'A18',
                                'A28',
                                'A32',
                                'A37',
                                'A39',
                                'A46',
                                'A47',
                                'A49',
                                'A49C',
                                'A50',
                                'A50C',
                              ].includes(row.id);
                              return (
                                <tr
                                  key={row.id}
                                  className={`border-t ${
                                    sectionBreak ? 'border-t-2 border-t-slate-300' : ''
                                  } ${emphasize ? 'bg-slate-50 font-semibold' : ''}`}
                                >
                                  <td className="p-2 text-muted-foreground sticky left-0 bg-inherit z-10">
                                    {row.id}
                                  </td>
                                  <td className="p-2 sticky left-10 bg-inherit z-10">
                                    {isPnlCentralExpenseLineId(row.id) ? (
                                      <PnlExpenseBreakdownHover
                                        lineId={row.id}
                                        label={row.label}
                                        consolidated={pnlConsolidated}
                                        periodMode="MTD"
                                        periodKey=""
                                        hospitalId={pnlHospitalParam}
                                      />
                                    ) : isPnlIpdDirectCostLineId(row.id) ? (
                                      <PnlIpdDirectCostBreakdownHover
                                        lineId={row.id}
                                        label={row.label}
                                        periodMode="MTD"
                                        periodKey=""
                                        hospitalId={pnlHospitalParam}
                                      />
                                    ) : (
                                      row.label
                                    )}
                                  </td>
                                  {mom.months.map((m) => {
                                    const cell = m.lines.find((l) => l.id === row.id);
                                    return (
                                      <td
                                        key={`${m.month}-${row.id}`}
                                        className="p-2 text-right whitespace-nowrap"
                                      >
                                        {isPnlCentralExpenseLineId(row.id) ? (
                                          <PnlExpenseBreakdownHover
                                            lineId={row.id}
                                            label={row.label}
                                            consolidated={pnlConsolidated}
                                            periodMode="MTD"
                                            periodKey={m.month}
                                            hospitalId={pnlHospitalParam}
                                          >
                                            <PnlEditableCell
                                              cell={cell}
                                              rowFormat={row.format}
                                              periodKey={m.month}
                                              periodMode="MTD"
                                              hospitalId={pnlHospitalParam}
                                              pnlQueryKey={pnlQueryKey}
                                              editable={canEditFinancialStatements}
                                            />
                                          </PnlExpenseBreakdownHover>
                                        ) : isPnlIpdDirectCostLineId(row.id) ? (
                                          <PnlIpdDirectCostBreakdownHover
                                            lineId={row.id}
                                            label={row.label}
                                            periodMode="MTD"
                                            periodKey={m.month}
                                            hospitalId={pnlHospitalParam}
                                          >
                                            <PnlEditableCell
                                              cell={cell}
                                              rowFormat={row.format}
                                              periodKey={m.month}
                                              periodMode="MTD"
                                              hospitalId={pnlHospitalParam}
                                              pnlQueryKey={pnlQueryKey}
                                              editable={canEditFinancialStatements}
                                            />
                                          </PnlIpdDirectCostBreakdownHover>
                                        ) : isPnlAestheticProceduresLineId(row.id) ? (
                                          <PnlAestheticProceduresHover
                                            periodMode="MTD"
                                            periodKey={m.month}
                                            hospitalId={pnlHospitalParam}
                                          >
                                            <PnlEditableCell
                                              cell={cell}
                                              rowFormat={row.format}
                                              periodKey={m.month}
                                              periodMode="MTD"
                                              hospitalId={pnlHospitalParam}
                                              pnlQueryKey={pnlQueryKey}
                                              editable={canEditFinancialStatements}
                                            />
                                          </PnlAestheticProceduresHover>
                                        ) : (
                                          <PnlEditableCell
                                            cell={cell}
                                            rowFormat={row.format}
                                            periodKey={m.month}
                                            periodMode="MTD"
                                            hospitalId={pnlHospitalParam}
                                            pnlQueryKey={pnlQueryKey}
                                            editable={canEditFinancialStatements}
                                          />
                                        )}
                                      </td>
                                    );
                                  })}
                                  <td className="p-2 text-right whitespace-nowrap bg-slate-50/80 font-medium">
                                    {row.id === 'A9' ? (
                                      <span className="text-muted-foreground">—</span>
                                    ) : (
                                      <PnlEditableCell
                                        cell={mom.totalLines?.find((l) => l.id === row.id)}
                                        rowFormat={row.format}
                                        periodKey=""
                                        periodMode="MTD"
                                        hospitalId={pnlHospitalParam}
                                        pnlQueryKey={pnlQueryKey}
                                        editable={false}
                                      />
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        MoM uses the same line logic as MTD for each calendar month. Current month is
                        month-to-date through today. The <strong>FY Total</strong> column sums leaf metrics
                        across months and recalculates derived lines and margins (IPD revenue growth % is
                        not shown for the total). Double-click a value to override; right-click an
                        amber cell to revert to the system figure. Dependent lines recalculate automatically.
                        Dotted metric names: central expense lines, IPD direct costs (A19/A21/A23/A25/A27), and
                        A2A show breakdowns on hover.
                      </p>
                    </div>
                  );
                })()
              ) : (
                <div className="space-y-4">
                  <div className="overflow-x-auto">
                    <table className="min-w-full border text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="p-2 text-left w-16">ID</th>
                          <th className="p-2 text-left">Metric</th>
                          <th className="p-2 text-right">Value</th>
                          <th className="p-2 text-left">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(pnlReport as HospitalPnlResponse).lines.map((row, idx) => {
                          const single = pnlReport as HospitalPnlResponse;
                          const pnlHospitalParam =
                            reportHospitalId === 'all' ? 'all' : reportHospitalId;
                          const pnlConsolidated =
                            single.consolidated ??
                            (reportHospitalId === 'all' || reportHospitalId == null);
                          const prev = idx > 0 ? single.lines[idx - 1] : null;
                          const sectionBreak = prev && prev.section !== row.section;
                          const emphasize = [
                            'A14',
                            'A18',
                            'A28',
                            'A32',
                            'A37',
                            'A39',
                            'A46',
                            'A47',
                            'A49',
                            'A49C',
                            'A50',
                            'A50C',
                          ].includes(row.id);
                          return (
                            <tr
                              key={row.id}
                              className={`border-t ${
                                sectionBreak ? 'border-t-2 border-t-slate-300' : ''
                              } ${emphasize ? 'bg-slate-50 font-semibold' : ''}`}
                            >
                              <td className="p-2 text-muted-foreground">{row.id}</td>
                              <td className="p-2">
                                {isPnlCentralExpenseLineId(row.id) ? (
                                  <PnlExpenseBreakdownHover
                                    lineId={row.id}
                                    label={row.label}
                                    consolidated={pnlConsolidated}
                                    periodMode={single.period.mode}
                                    periodKey={single.period.periodKey}
                                    fyStartYear={
                                      single.period.mode === 'YTD'
                                        ? pnlFyStartYear
                                        : undefined
                                    }
                                    hospitalId={pnlHospitalParam}
                                  />
                                ) : isPnlIpdDirectCostLineId(row.id) ? (
                                  <PnlIpdDirectCostBreakdownHover
                                    lineId={row.id}
                                    label={row.label}
                                    periodMode={single.period.mode}
                                    periodKey={single.period.periodKey}
                                    fyStartYear={
                                      single.period.mode === 'YTD' ? pnlFyStartYear : undefined
                                    }
                                    hospitalId={pnlHospitalParam}
                                  />
                                ) : isPnlAestheticProceduresLineId(row.id) ? (
                                  <PnlAestheticProceduresHover
                                    periodMode={single.period.mode}
                                    periodKey={single.period.periodKey}
                                    fyStartYear={
                                      single.period.mode === 'YTD' ? pnlFyStartYear : undefined
                                    }
                                    hospitalId={pnlHospitalParam}
                                  />
                                ) : (
                                  <div>{row.label}</div>
                                )}
                                {row.formula ? (
                                  <div className="text-xs text-muted-foreground">{row.formula}</div>
                                ) : null}
                              </td>
                              <td className="p-2 text-right whitespace-nowrap">
                                {isPnlCentralExpenseLineId(row.id) ? (
                                  <PnlExpenseBreakdownHover
                                    lineId={row.id}
                                    label={row.label}
                                    consolidated={pnlConsolidated}
                                    periodMode={single.period.mode}
                                    periodKey={single.period.periodKey}
                                    fyStartYear={
                                      single.period.mode === 'YTD'
                                        ? pnlFyStartYear
                                        : undefined
                                    }
                                    hospitalId={pnlHospitalParam}
                                  >
                                    <PnlEditableCell
                                      cell={row}
                                      rowFormat={row.format}
                                      periodKey={single.period.periodKey}
                                      periodMode={single.period.mode}
                                      hospitalId={pnlHospitalParam}
                                      pnlQueryKey={pnlQueryKey}
                                      editable={canEditFinancialStatements}
                                    />
                                  </PnlExpenseBreakdownHover>
                                ) : isPnlIpdDirectCostLineId(row.id) ? (
                                  <PnlIpdDirectCostBreakdownHover
                                    lineId={row.id}
                                    label={row.label}
                                    periodMode={single.period.mode}
                                    periodKey={single.period.periodKey}
                                    fyStartYear={
                                      single.period.mode === 'YTD' ? pnlFyStartYear : undefined
                                    }
                                    hospitalId={pnlHospitalParam}
                                  >
                                    <PnlEditableCell
                                      cell={row}
                                      rowFormat={row.format}
                                      periodKey={single.period.periodKey}
                                      periodMode={single.period.mode}
                                      hospitalId={pnlHospitalParam}
                                      pnlQueryKey={pnlQueryKey}
                                      editable={canEditFinancialStatements}
                                    />
                                  </PnlIpdDirectCostBreakdownHover>
                                ) : isPnlAestheticProceduresLineId(row.id) ? (
                                  <PnlAestheticProceduresHover
                                    periodMode={single.period.mode}
                                    periodKey={single.period.periodKey}
                                    fyStartYear={
                                      single.period.mode === 'YTD' ? pnlFyStartYear : undefined
                                    }
                                    hospitalId={pnlHospitalParam}
                                  >
                                    <PnlEditableCell
                                      cell={row}
                                      rowFormat={row.format}
                                      periodKey={single.period.periodKey}
                                      periodMode={single.period.mode}
                                      hospitalId={pnlHospitalParam}
                                      pnlQueryKey={pnlQueryKey}
                                      editable={canEditFinancialStatements}
                                    />
                                  </PnlAestheticProceduresHover>
                                ) : (
                                  <PnlEditableCell
                                    cell={row}
                                    rowFormat={row.format}
                                    periodKey={single.period.periodKey}
                                    periodMode={single.period.mode}
                                    hospitalId={pnlHospitalParam}
                                    pnlQueryKey={pnlQueryKey}
                                    editable={canEditFinancialStatements}
                                  />
                                )}
                              </td>
                              <td className="p-2 text-xs text-muted-foreground max-w-[280px]">
                                {row.note || '—'}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    A13 includes OPD, Lab, Pharma, Day Care, and Additional Services. A13A is Finance → Additional
                    Revenue (by service month). Gross Revenue A14 = A8 + A13 + A13A. Double-click a value to
                    override; right-click an amber cell to revert. Dependent lines recalculate automatically.
                    Performance marketing (A34) is allocated by unit share of aesthetic IPDs (A2) × online
                    marketing spend per service month. Consolidated scope splits hospital vs Hospital–Central rows
                    (salary, misc, capex, one-time fees) and includes all central-unit tagged expenses.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {reportType === 'cash_flow' && (
          <CashFlowReportPanel
            mode={pnlMode}
            report={cashFlowReport}
            loading={loadingCashFlow}
            error={cashFlowError}
            queryError={cashFlowQueryError}
            queryKey={cfQueryKey}
            hospitalId={reportHospitalId === 'all' || !reportHospitalId ? 'all' : reportHospitalId}
            editable={canEditFinancialStatements}
            fyStartYear={pnlFyStartYear}
          />
        )}

        {reportType === 'sales_attribution' && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-emerald-600" />
                Sales Report
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Count and revenue by CRM sales person, IPD/Day Care form sales person, partner lead, and referrer —
                across OPD, IPD, Lab, Pharma, and Day Care. OPD/Lab/Pharma use bill date; IPD uses discharge date.
                For IPD, admission-form sales, partner, or referrer attribution takes priority over CRM; CRM is
                used only as a fallback from a lead that existed by the admission date. When partner lead is set
                on the form, that conversion is counted under Partner Lead only (not under Form Sales Person),
                even if a sales person name is also filled. Dummy IPD stays are excluded. Unassigned revenue is
                in <strong>Others</strong> so each section matches period category totals. Click a name to see
                case-wise revenue.
              </p>
            </CardHeader>
            <CardContent className="space-y-8">
              {loadingSalesAttribution ? (
                <div className="text-sm text-artis-slate">Loading…</div>
              ) : !salesAttribution ? (
                <div className="text-sm text-artis-slate">No data for this range.</div>
              ) : (
                <>
                  {salesAttribution.periodTotals && (
                    <div>
                      <h3 className="text-sm font-semibold mb-2">Period category totals</h3>
                      <p className="text-xs text-muted-foreground mb-2">
                        Same basis as Revenue Breakdown. Each table below (including Others) adds up to these figures.
                      </p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-sm">
                        {(
                          [
                            ['OPD', salesAttribution.periodTotals.opd],
                            ['IPD', salesAttribution.periodTotals.ipd],
                            ['Lab', salesAttribution.periodTotals.lab],
                            ['Pharma', salesAttribution.periodTotals.pharma],
                            ['Daycare', salesAttribution.periodTotals.daycare],
                            ['Total', salesAttribution.periodTotals.total],
                          ] as Array<[string, number]>
                        ).map(([label, value]) => (
                          <div key={label} className="rounded border bg-muted/20 p-3">
                            <div className="text-xs text-muted-foreground">{label}</div>
                            <div className="font-semibold tabular-nums mt-1">
                              ₹{Number(value || 0).toLocaleString('en-IN')}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                {(
                  [
                    { title: 'CRM Sales Person', dimension: 'crm' as SalesAttributionDimension, rows: salesAttribution.crmSales },
                    {
                      title: 'IPD / Day Care Form Sales Person',
                      dimension: 'form_sales' as SalesAttributionDimension,
                      rows: salesAttribution.formSales,
                    },
                    { title: 'Partner Lead', dimension: 'partner_lead' as SalesAttributionDimension, rows: salesAttribution.partnerLead },
                    { title: 'Referrer Name', dimension: 'referrer' as SalesAttributionDimension, rows: salesAttribution.referrer },
                  ] as Array<{ title: string; dimension: SalesAttributionDimension; rows: SalesAttributionRow[] }>
                ).map((sec) => (
                  <div key={sec.title}>
                    <h3 className="text-sm font-semibold mb-2">{sec.title}</h3>
                    {!sec.rows.length ? (
                      <div className="text-sm text-artis-slate mb-4">No rows.</div>
                    ) : (
                      <div className="overflow-x-auto mb-2">
                        <table className="min-w-[1100px] w-full text-sm border rounded">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="p-2 text-left">Name</th>
                              <th className="p-2 text-right">OPD #</th>
                              <th className="p-2 text-right">OPD ₹</th>
                              <th className="p-2 text-right">IPD #</th>
                              <th className="p-2 text-right">IPD ₹</th>
                              <th className="p-2 text-right">Lab #</th>
                              <th className="p-2 text-right">Lab ₹</th>
                              <th className="p-2 text-right">Pharma #</th>
                              <th className="p-2 text-right">Pharma ₹</th>
                              <th className="p-2 text-right">Daycare #</th>
                              <th className="p-2 text-right">Daycare ₹</th>
                              <th className="p-2 text-right font-semibold">Total ₹</th>
                            </tr>
                          </thead>
                          <tbody>
                            {sec.rows.map((r) => {
                              const isOthers = r.name === 'Others';
                              return (
                              <tr key={`${sec.title}-${r.name}`} className={`border-t ${isOthers ? 'bg-amber-50/60' : ''}`}>
                                <td className="p-2 whitespace-nowrap font-medium">
                                  {isOthers ? (
                                    <span className="text-amber-900" title="Revenue not attributed to any name in this section">
                                      Others
                                    </span>
                                  ) : (
                                    <button
                                      type="button"
                                      className="text-blue-600 hover:underline text-left"
                                      onClick={() =>
                                        setSalesDrilldown({
                                          dimension: sec.dimension,
                                          name: r.name,
                                          label: sec.title,
                                        })
                                      }
                                    >
                                      {r.name}
                                    </button>
                                  )}
                                </td>
                                <td className="p-2 text-right">{r.opd.count}</td>
                                <td className="p-2 text-right">₹{Number(r.opd.revenue || 0).toLocaleString('en-IN')}</td>
                                <td className="p-2 text-right">{r.ipd.count}</td>
                                <td className="p-2 text-right">₹{Number(r.ipd.revenue || 0).toLocaleString('en-IN')}</td>
                                <td className="p-2 text-right">{r.lab.count}</td>
                                <td className="p-2 text-right">₹{Number(r.lab.revenue || 0).toLocaleString('en-IN')}</td>
                                <td className="p-2 text-right">{r.pharma.count}</td>
                                <td className="p-2 text-right">₹{Number(r.pharma.revenue || 0).toLocaleString('en-IN')}</td>
                                <td className="p-2 text-right">{r.daycare.count}</td>
                                <td className="p-2 text-right">₹{Number(r.daycare.revenue || 0).toLocaleString('en-IN')}</td>
                                <td className="p-2 text-right font-semibold">
                                  ₹{Number(r.totalRevenue || 0).toLocaleString('en-IN')}
                                </td>
                              </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}

                <div className="border-t pt-6">
                  <div className="flex flex-wrap items-end justify-between gap-2 mb-3">
                    <div>
                      <h3 className="text-sm font-semibold">CRM missed IPD conversions</h3>
                      <p className="text-xs text-muted-foreground mt-1">
                        A CRM lead existed before admission, but the IPD form credits another sales person,
                        partner, or referrer. Cumulative for all discharged IPD cases (not limited to the
                        selected report period).
                      </p>
                    </div>
                    <div className="text-sm text-right">
                      <span className="font-medium">
                        {salesAttribution.crmMissedIpdConversions?.length || 0} cases
                      </span>
                      <span className="text-muted-foreground ml-2">
                        ₹
                        {(salesAttribution.crmMissedIpdConversions || [])
                          .reduce((sum, row) => sum + Number(row.ipdRevenue || 0), 0)
                          .toLocaleString('en-IN')}
                      </span>
                    </div>
                  </div>

                  {!salesAttribution.crmMissedIpdConversions?.length ? (
                    <div className="text-sm text-artis-slate">No missed CRM IPD conversions on record.</div>
                  ) : (
                    <div className="overflow-auto max-h-[520px] border rounded">
                      <table className="min-w-[1450px] w-full text-sm">
                        <thead className="bg-gray-50 sticky top-0 z-10">
                          <tr>
                            <th className="p-2 text-left">IPD #</th>
                            <th className="p-2 text-left">Patient</th>
                            <th className="p-2 text-left">Admission</th>
                            <th className="p-2 text-left">Discharge</th>
                            <th className="p-2 text-left">CRM sales person</th>
                            <th className="p-2 text-left">CRM lead date</th>
                            <th className="p-2 text-left">Lead source / campaign</th>
                            <th className="p-2 text-left">Form sales person</th>
                            <th className="p-2 text-left">Partner</th>
                            <th className="p-2 text-left">Referrer</th>
                            <th className="p-2 text-right">IPD ₹</th>
                          </tr>
                        </thead>
                        <tbody>
                          {salesAttribution.crmMissedIpdConversions.map((row) => (
                            <tr key={row.admissionId} className="border-t hover:bg-amber-50/40">
                              <td className="p-2 whitespace-nowrap">
                                <Link
                                  to={`${portalBase}/ipd/${row.admissionId}`}
                                  className="font-medium text-primary hover:underline"
                                  title="Open IPD case"
                                >
                                  {row.ipdNumber || 'View IPD'}
                                </Link>
                              </td>
                              <td className="p-2">
                                <div className="font-medium">{row.patientName}</div>
                                <div className="text-xs text-muted-foreground">{row.phone || '—'}</div>
                              </td>
                              <td className="p-2 whitespace-nowrap">
                                {format(new Date(row.admissionDate), 'dd/MM/yyyy')}
                              </td>
                              <td className="p-2 whitespace-nowrap">
                                {format(new Date(row.dischargeDate), 'dd/MM/yyyy')}
                              </td>
                              <td className="p-2 font-medium">{row.crmSalesPersonName}</td>
                              <td className="p-2 whitespace-nowrap">
                                {format(new Date(row.crmLeadCreatedAt), 'dd/MM/yyyy')}
                              </td>
                              <td className="p-2">
                                <div>{row.leadSource?.replace(/_/g, ' ') || '—'}</div>
                                {row.campaignName ? (
                                  <div className="text-xs text-muted-foreground">{row.campaignName}</div>
                                ) : null}
                              </td>
                              <td className="p-2">{row.formSalesPersonName || '—'}</td>
                              <td className="p-2">{row.partnerLeadName || '—'}</td>
                              <td className="p-2">{row.referrerName || '—'}</td>
                              <td className="p-2 text-right font-semibold whitespace-nowrap">
                                ₹{Number(row.ipdRevenue || 0).toLocaleString('en-IN')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        <Dialog open={!!salesDrilldown} onOpenChange={(open) => !open && setSalesDrilldown(null)}>
          <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {salesDrilldown
                  ? `${salesDrilldown.label}: ${salesDrilldown.name}`
                  : 'Cases'}
              </DialogTitle>
            </DialogHeader>
            {loadingSalesAttributionCases ? (
              <div className="text-sm text-artis-slate py-6">Loading cases…</div>
            ) : !salesAttributionCases?.cases?.length ? (
              <div className="text-sm text-artis-slate py-6">No cases found for this name in the selected range.</div>
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-sm">
                  <div className="rounded border p-2">
                    <div className="text-xs text-muted-foreground">Cases</div>
                    <div className="font-semibold tabular-nums">{salesAttributionCases.totals.caseCount}</div>
                  </div>
                  <div className="rounded border p-2">
                    <div className="text-xs text-muted-foreground">OPD ₹</div>
                    <div className="font-semibold tabular-nums">
                      ₹{Number(salesAttributionCases.totals.opdRevenue || 0).toLocaleString('en-IN')}
                    </div>
                  </div>
                  <div className="rounded border p-2">
                    <div className="text-xs text-muted-foreground">IPD ₹</div>
                    <div className="font-semibold tabular-nums">
                      ₹{Number(salesAttributionCases.totals.ipdRevenue || 0).toLocaleString('en-IN')}
                    </div>
                  </div>
                  <div className="rounded border p-2">
                    <div className="text-xs text-muted-foreground">Lab ₹</div>
                    <div className="font-semibold tabular-nums">
                      ₹{Number(salesAttributionCases.totals.labRevenue || 0).toLocaleString('en-IN')}
                    </div>
                  </div>
                  <div className="rounded border p-2">
                    <div className="text-xs text-muted-foreground">Pharma ₹</div>
                    <div className="font-semibold tabular-nums">
                      ₹{Number(salesAttributionCases.totals.pharmaRevenue || 0).toLocaleString('en-IN')}
                    </div>
                  </div>
                  <div className="rounded border p-2">
                    <div className="text-xs text-muted-foreground">Total ₹</div>
                    <div className="font-semibold tabular-nums">
                      ₹{Number(salesAttributionCases.totals.totalRevenue || 0).toLocaleString('en-IN')}
                    </div>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  <table className="min-w-[900px] w-full text-sm border rounded">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="p-2 text-left">Type</th>
                        <th className="p-2 text-left">Case #</th>
                        <th className="p-2 text-left">Patient</th>
                        <th className="p-2 text-left">Date</th>
                        <th className="p-2 text-right">OPD ₹</th>
                        <th className="p-2 text-right">Lab ₹</th>
                        <th className="p-2 text-right">Pharma ₹</th>
                        <th className="p-2 text-right">IPD ₹</th>
                        <th className="p-2 text-right">Daycare ₹</th>
                        <th className="p-2 text-right font-semibold">Total ₹</th>
                      </tr>
                    </thead>
                    <tbody>
                      {salesAttributionCases.cases.map((c) => (
                        <tr key={`${c.channel}-${c.caseId}`} className="border-t">
                          <td className="p-2 whitespace-nowrap">
                            {c.channel === 'OPD_BILL' ? 'OPD Bill' : c.channel === 'IPD' ? 'IPD' : 'Day Care'}
                          </td>
                          <td className="p-2 whitespace-nowrap">{c.caseNumber || '—'}</td>
                          <td className="p-2 whitespace-nowrap">
                            <div>{c.patientName}</div>
                            {c.phone ? <div className="text-xs text-muted-foreground">{c.phone}</div> : null}
                          </td>
                          <td className="p-2 whitespace-nowrap">
                            {c.caseDate ? format(new Date(c.caseDate), 'dd/MM/yyyy') : '—'}
                          </td>
                          <td className="p-2 text-right">₹{Number(c.opdRevenue || 0).toLocaleString('en-IN')}</td>
                          <td className="p-2 text-right">₹{Number(c.labRevenue || 0).toLocaleString('en-IN')}</td>
                          <td className="p-2 text-right">₹{Number(c.pharmaRevenue || 0).toLocaleString('en-IN')}</td>
                          <td className="p-2 text-right">₹{Number(c.ipdRevenue || 0).toLocaleString('en-IN')}</td>
                          <td className="p-2 text-right">₹{Number(c.daycareRevenue || 0).toLocaleString('en-IN')}</td>
                          <td className="p-2 text-right font-semibold">
                            ₹{Number(c.totalRevenue || 0).toLocaleString('en-IN')}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {reportType === 'ipd_profitability' && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-emerald-600" />
                IPD Profitability — Discharged Patients
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                One row per admission (IPD number) discharged in the date range. <strong>Total Rev</strong> uses
                actual cash collected when third-party pending is marked Collected (desk deposits + TPA settled
                amount); otherwise bill net. <strong>Settle drop %</strong> shows the shortfall vs net billed when
                TPA paid less than billed pending.
                Lead / Reg. Source uses the lead source captured on the IPD admission form when set; otherwise CRM lead source
                (phone-matched lead on/before admission); otherwise patient Registration Source. Expense
                columns match the Patient (IPD) expense dropdown (Surgeon, Anaesthetist, Asst Surg, Consultant, Equip
                Rent, Implant, Blood, Referral, Corp Disc Adj, Outside Lab, EMI/Reimb, B2B Partner, Custom). Older
                categories removed from the dropdown still count under Other Exp. Lab Cost (Internal), Ext Lab Cost,
                and Pharma Cost match Expenses → IPD admission module costs. Total Exp = patient expenses + Lab
                (Internal) + Ext Lab + Pharma. <strong>Plastic Surgery</strong> can be set Yes/No here (admin) and
                drives Plastic Surgery revenue &amp; profitability reports.
              </p>
            </CardHeader>
            <CardContent>
              {loadingIpdProfitability ? (
                <div className="text-sm text-artis-slate">Loading…</div>
              ) : !ipdProfitability?.rows?.length ? (
                <div className="text-sm text-artis-slate">No discharged IPD admissions found for this range.</div>
              ) : (
                <>
                  <ReportSummaryTiles variant={summaryTilesVariant} tiles={ipdProfitabilitySummaryTiles} />
                <div className="overflow-x-auto">
                  <table className="min-w-[1600px] w-full text-sm border rounded">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="p-2 text-left">IPD No</th>
                        <th className="p-2 text-left">Patient</th>
                        <th className="p-2 text-left">Patient phone</th>
                        <th className="p-2 text-left">Doctor</th>
                        <th className="p-2 text-left">Anesthetist</th>
                        <th
                          className="p-2 text-left min-w-[160px]"
                          title="From IPD admission form — package surgery name(s)"
                        >
                          Surgery name
                        </th>
                        <th
                          className="p-2 text-left whitespace-nowrap"
                          title="Editable by admin — counts this IPD in Plastic Surgery reports when Yes"
                        >
                          Plastic Surgery
                        </th>
                        <th className="p-2 text-left">Referrer / Sales / Partner</th>
                        <th
                          className="p-2 text-left whitespace-nowrap"
                          title="Admission lead source when set; else CRM phone-matched lead; else patient registration source"
                        >
                          Lead / Reg. Source
                        </th>
                        <th className="p-2 text-left">UHID</th>
                        <th className="p-2 text-left">Ward</th>
                        <th className="p-2 text-left">Bed</th>
                        <th className="p-2 text-left">Admission</th>
                        <th className="p-2 text-left">Discharge</th>

                        <th className="p-2 text-right">Room</th>
                        <th className="p-2 text-right">Visit</th>
                        <th className="p-2 text-right">Surgery</th>
                        <th className="p-2 text-right">Lab</th>
                        <th className="p-2 text-right">Pharma</th>
                        <th className="p-2 text-right">Consumable</th>
                        <th className="p-2 text-right">Misc</th>
                        <th className="p-2 text-right">Other</th>
                        <th className="p-2 text-right font-semibold">Total Rev</th>
                        <th className="p-2 text-right whitespace-nowrap" title="Bill net before TPA settlement adjustment">
                          Net billed
                        </th>
                        <th
                          className="p-2 text-right whitespace-nowrap"
                          title="% drop when third-party settled below billed pending (bank + TDS)"
                        >
                          Settle drop %
                        </th>

                        <th className="p-2 text-right">Surgeon</th>
                        <th className="p-2 text-right">Anaesth.</th>
                        <th className="p-2 text-right">Asst Surg.</th>
                        <th className="p-2 text-right">Consult.</th>
                        <th className="p-2 text-right">Equip Rent</th>
                        <th className="p-2 text-right">Implant</th>
                        <th className="p-2 text-right">Blood</th>
                        <th className="p-2 text-right">Referral</th>
                        <th className="p-2 text-right">Corp Disc Adj</th>
                        <th className="p-2 text-right">Outside Lab</th>
                        <th className="p-2 text-right whitespace-nowrap">EMI/Reimb.</th>
                        <th className="p-2 text-right whitespace-nowrap" title="B2B Partner Payout (Carelane, Hexa, Practo, Surgenix, …)">
                          B2B Partner
                        </th>
                        <th className="p-2 text-right">Custom</th>
                        <th
                          className="p-2 text-right"
                          title="Legacy categories no longer on the expense dropdown (agent, non-bill visit/cons, lab payout, …)"
                        >
                          Other Exp
                        </th>
                        <th
                          className="p-2 text-right whitespace-nowrap"
                          title="Same Lab cost (Internal) as Expenses → IPD admission costs"
                        >
                          Lab (Internal)
                        </th>
                        <th
                          className="p-2 text-right whitespace-nowrap"
                          title="External lab vendor cost (included in Total Exp)"
                        >
                          Ext Lab Cost
                        </th>
                        <th className="p-2 text-right">Pharma Cost</th>
                        <th className="p-2 text-right font-semibold">Total Exp</th>

                        <th className="p-2 text-right font-semibold">Profit</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ipdProfitability.rows.map((r: any) => (
                        <tr key={r.admissionId} className="border-t">
                          <td className="p-2 whitespace-nowrap">{r.ipdNumber}</td>
                          <td className="p-2 whitespace-nowrap">{r.patientName}</td>
                          <td className="p-2 whitespace-nowrap">{r.phone ?? '—'}</td>
                          <td className="p-2 whitespace-nowrap">{r.doctorName ?? '—'}</td>
                          <td className="p-2 whitespace-nowrap">{r.anaesthetistName ?? '—'}</td>
                          <td
                            className="p-2 align-top min-w-[160px] max-w-[240px] text-xs leading-snug break-words"
                            title={r.surgeryNames ?? undefined}
                          >
                            {r.surgeryNames?.trim() ? r.surgeryNames : '—'}
                          </td>
                          <td className="p-2 whitespace-nowrap">
                            {canEditIpdPlasticSurgery ? (
                              <Select
                                value={r.isPlasticSurgery ? 'yes' : 'no'}
                                disabled={
                                  updateIpdPlasticSurgeryMut.isPending &&
                                  updateIpdPlasticSurgeryMut.variables?.admissionId === r.admissionId
                                }
                                onValueChange={(v) => {
                                  const next = v === 'yes';
                                  if (next === Boolean(r.isPlasticSurgery)) return;
                                  updateIpdPlasticSurgeryMut.mutate({
                                    admissionId: r.admissionId,
                                    isPlasticSurgery: next,
                                  });
                                }}
                              >
                                <SelectTrigger className="h-8 w-[88px]">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="yes">Yes</SelectItem>
                                  <SelectItem value="no">No</SelectItem>
                                </SelectContent>
                              </Select>
                            ) : (
                              r.isPlasticSurgery ? 'Yes' : 'No'
                            )}
                          </td>
                          <td className="p-2 align-top min-w-[140px] max-w-[200px]">
                            {r.referrerName || r.salesPersonName || r.partnerLeadName || r.leadAttribution ? (
                              <div className="space-y-0.5 text-xs leading-snug break-words" title={r.leadAttribution || undefined}>
                                {r.referrerName ? (
                                  <div>
                                    <span className="text-muted-foreground">Referrer:</span> {r.referrerName}
                                  </div>
                                ) : null}
                                {r.salesPersonName ? (
                                  <div>
                                    <span className="text-muted-foreground">Sales:</span> {r.salesPersonName}
                                  </div>
                                ) : null}
                                {r.partnerLeadName ? (
                                  <div>
                                    <span className="text-muted-foreground">Partner:</span> {r.partnerLeadName}
                                  </div>
                                ) : null}
                                {!r.referrerName && !r.salesPersonName && !r.partnerLeadName && r.leadAttribution
                                  ? String(r.leadAttribution)
                                      .split(/\n|; /)
                                      .filter(Boolean)
                                      .map((line: string) => (
                                        <div key={line}>{line}</div>
                                      ))
                                  : null}
                              </div>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td
                            className="p-2 whitespace-nowrap"
                            title={
                              r.crmLeadSource
                                ? `CRM lead: ${r.crmLeadSource}`
                                : r.registrationSource
                                  ? `Registration source: ${r.registrationSource}`
                                  : undefined
                            }
                          >
                            {r.leadOrRegistrationSource
                              ? String(r.leadOrRegistrationSource).replace(/_/g, ' ')
                              : '—'}
                          </td>
                          <td className="p-2 whitespace-nowrap">{r.uhid ?? '—'}</td>
                          <td className="p-2 whitespace-nowrap">{r.wardType}</td>
                          <td className="p-2 whitespace-nowrap">{r.bedNumber ?? '—'}</td>
                          <td className="p-2 whitespace-nowrap">{format(new Date(r.admissionDate), 'dd/MM/yyyy')}</td>
                          <td className="p-2 whitespace-nowrap">{format(new Date(r.dischargeDate), 'dd/MM/yyyy')}</td>

                          <td className="p-2 text-right">₹{Number(r.roomRevenue || 0).toLocaleString()}</td>
                          <td className="p-2 text-right">₹{Number(r.visitRevenue || 0).toLocaleString()}</td>
                          <td className="p-2 text-right">₹{Number(r.surgeryRevenue || 0).toLocaleString()}</td>
                          <td className="p-2 text-right">₹{Number(r.labRevenue || 0).toLocaleString()}</td>
                          <td className="p-2 text-right">₹{Number(r.pharmaRevenue || 0).toLocaleString()}</td>
                          <td className="p-2 text-right">₹{Number(r.consumableRevenue || 0).toLocaleString()}</td>
                          <td className="p-2 text-right">₹{Number(r.miscRevenue || 0).toLocaleString()}</td>
                          <td className="p-2 text-right">₹{Number(r.otherRevenue || 0).toLocaleString()}</td>
                          <td className="p-2 text-right font-semibold">
                            <div>₹{Number(r.totalRevenue || 0).toLocaleString()}</div>
                            <div
                              className={
                                r.billPaymentStatus === 'Paid'
                                  ? 'text-xs font-medium text-emerald-700'
                                  : 'text-xs font-medium text-amber-700'
                              }
                            >
                              {r.billPaymentStatus === 'Paid' ? 'Paid' : 'Unpaid'}
                            </div>
                          </td>
                          <td className="p-2 text-right text-muted-foreground">
                            {r.settlementDropPercent != null ? (
                              <>₹{Number(r.netBilledAmount || 0).toLocaleString()}</>
                            ) : (
                              '—'
                            )}
                          </td>
                          <td className="p-2 text-right">
                            {r.settlementDropPercent != null ? (
                              <span className="font-medium text-amber-800">
                                {Number(r.settlementDropPercent).toFixed(1)}%
                              </span>
                            ) : (
                              '—'
                            )}
                          </td>

                          <ProfitabilityAmountCell amount={r.surgeonPayout} status={r.surgeonPayoutStatus} />
                          <ProfitabilityAmountCell amount={r.anaesthetistPayout} status={r.anaesthetistPayoutStatus} />
                          <ProfitabilityAmountCell amount={r.assistantSurgeonPayout} status={r.assistantSurgeonPayoutStatus} />
                          <ProfitabilityAmountCell amount={r.visitingConsultantFee} status={r.visitingConsultantFeeStatus} />
                          <ProfitabilityAmountCell amount={r.medicalEquipmentRentalPatient} status={r.medicalEquipmentRentalPatientStatus} />
                          <ProfitabilityAmountCell amount={r.implantCostHospital} status={r.implantCostHospitalStatus} />
                          <ProfitabilityAmountCell amount={r.bloodProcurement} status={r.bloodProcurementStatus} />
                          <ProfitabilityAmountCell amount={r.referralPayout} status={r.referralPayoutStatus} />
                          <ProfitabilityAmountCell amount={r.corporateDiscountAdjustment} status={r.corporateDiscountAdjustmentStatus} />
                          <ProfitabilityAmountCell amount={r.outsideLabPayout} status={r.outsideLabPayoutStatus} />
                          <ProfitabilityAmountCell amount={r.emiFinanceCharges} status={r.emiFinanceChargesStatus} />
                          <ProfitabilityAmountCell amount={r.b2bPartnerPayout} status={r.b2bPartnerPayoutStatus} />
                          <ProfitabilityAmountCell amount={r.customPayout} status={r.customPayoutStatus} />
                          <ProfitabilityAmountCell amount={r.otherPatientExpense} status={r.otherPatientExpenseStatus} />
                          <td className="p-2 text-right">₹{Number(r.internalLabCost || 0).toLocaleString()}</td>
                          <td className="p-2 text-right">₹{Number(r.externalLabVendorCost || 0).toLocaleString()}</td>
                          <td className="p-2 text-right">₹{Number(r.pharmaCost || 0).toLocaleString()}</td>
                          <ProfitabilityAmountCell
                            amount={r.totalExpense}
                            status={r.patientExpensePaymentStatus}
                            strong
                          />

                          <td className="p-2 text-right font-semibold">₹{Number(r.profit || 0).toLocaleString()}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {reportType === 'plastic_surgery_revenue' && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-emerald-600" />
                Plastic Surgery Revenue
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Plastic OPD bills count fully under OPD. Separate lab/pharma bills for the same patient within 60 days
                of a plastic-surgeon OPD consultation count under Lab/Pharma. Final discharged IPD bill stays fully
                under IPD when the admission is marked <strong>Plastic Surgery = Yes</strong> (set on IPD Profitability).
                {plasticSurgeryRevenue?.plasticDoctorNames?.length ? (
                  <> Matched OPD doctors: {plasticSurgeryRevenue.plasticDoctorNames.join(', ')}.</>
                ) : null}
              </p>
            </CardHeader>
            <CardContent>
              {loadingPlasticSurgeryRevenue ? (
                <div className="text-sm text-artis-slate">Loading…</div>
              ) : !plasticSurgeryRevenue?.rows?.length ? (
                <div className="text-sm text-artis-slate">No plastic surgery revenue found for this range.</div>
              ) : (
                <>
                  <ReportSummaryTiles variant={summaryTilesVariant} tiles={plasticSurgeryRevenueSummaryTiles} />
                  <div className="overflow-x-auto">
                    <table className="min-w-[1100px] w-full text-sm border rounded">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="p-2 text-left">Patient</th>
                          <th className="p-2 text-left">UHID</th>
                          <th className="p-2 text-left">Phone</th>
                          <th className="p-2 text-left">Doctor</th>
                          <th className="p-2 text-left">Specialisation</th>
                          <th className="p-2 text-right">OPD</th>
                          <th className="p-2 text-right">Lab</th>
                          <th className="p-2 text-right">Pharma</th>
                          <th className="p-2 text-right">IPD</th>
                          <th className="p-2 text-right font-semibold">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {plasticSurgeryRevenue.rows.map((r) => (
                          <tr key={r.patientId} className="border-t">
                            <td className="p-2">{r.patientName}</td>
                            <td className="p-2">{r.uhid ?? '—'}</td>
                            <td className="p-2">{r.phone ?? '—'}</td>
                            <td className="p-2">{r.doctorNames ?? '—'}</td>
                            <td className="p-2">{r.specialisations ?? '—'}</td>
                            <td className="p-2 text-right">₹{Number(r.opd).toLocaleString()}</td>
                            <td className="p-2 text-right">₹{Number(r.lab).toLocaleString()}</td>
                            <td className="p-2 text-right">₹{Number(r.pharma).toLocaleString()}</td>
                            <td className="p-2 text-right">₹{Number(r.ipd).toLocaleString()}</td>
                            <td className="p-2 text-right font-semibold">₹{Number(r.total).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {reportType === 'plastic_surgery_profitability' && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-emerald-600" />
                Plastic Surgery Profitability — Patient-wise
              </CardTitle>
              <p className="text-sm text-muted-foreground">
                Revenue as in the plastic surgery revenue report. Expenses are approved/paid IPD patient expenses,
                external lab vendor cost, and pharma cost for discharged admissions marked{' '}
                <strong>Plastic Surgery = Yes</strong> on IPD Profitability.
              </p>
            </CardHeader>
            <CardContent>
              {loadingPlasticSurgeryProfitability ? (
                <div className="text-sm text-artis-slate">Loading…</div>
              ) : !plasticSurgeryProfitability?.rows?.length ? (
                <div className="text-sm text-artis-slate">No plastic surgery patients found for this range.</div>
              ) : (
                <>
                  <ReportSummaryTiles variant={summaryTilesVariant} tiles={plasticSurgeryProfitabilitySummaryTiles} />
                  <div className="overflow-x-auto">
                    <table className="min-w-[1200px] w-full text-sm border rounded">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="p-2 text-left">Patient</th>
                          <th className="p-2 text-left">UHID</th>
                          <th className="p-2 text-left">Phone</th>
                          <th className="p-2 text-left">IPD No</th>
                          <th className="p-2 text-left">Admitting doctor</th>
                          <th className="p-2 text-right">OPD</th>
                          <th className="p-2 text-right">Lab</th>
                          <th className="p-2 text-right">Pharma</th>
                          <th className="p-2 text-right">IPD</th>
                          <th className="p-2 text-right font-semibold">Total Rev</th>
                          <th className="p-2 text-right font-semibold">Total Exp</th>
                          <th className="p-2 text-right font-semibold">Profit</th>
                        </tr>
                      </thead>
                      <tbody>
                        {plasticSurgeryProfitability.rows.map((r) => (
                          <tr key={r.patientId} className="border-t">
                            <td className="p-2">{r.patientName}</td>
                            <td className="p-2">{r.uhid ?? '—'}</td>
                            <td className="p-2">{r.phone ?? '—'}</td>
                            <td className="p-2">{r.ipdNumbers ?? '—'}</td>
                            <td className="p-2">{r.admittingDoctors ?? '—'}</td>
                            <td className="p-2 text-right">₹{Number(r.opd).toLocaleString()}</td>
                            <td className="p-2 text-right">₹{Number(r.lab).toLocaleString()}</td>
                            <td className="p-2 text-right">₹{Number(r.pharma).toLocaleString()}</td>
                            <td className="p-2 text-right">₹{Number(r.ipd).toLocaleString()}</td>
                            <td className="p-2 text-right font-semibold">₹{Number(r.totalRevenue).toLocaleString()}</td>
                            <td className="p-2 text-right font-semibold">₹{Number(r.totalExpense).toLocaleString()}</td>
                            <td className="p-2 text-right font-semibold">₹{Number(r.profit).toLocaleString()}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {reportType === 'revenue_breakdown' && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-emerald-600" />
                Revenue Breakdown — OPD / Lab / Pharma / Additional Services / IPD / DC
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-7 gap-4">
                <div className="p-3 bg-blue-50 rounded-lg">
                  <p className="text-xs text-gray-500">OPD</p>
                  <p className="text-xl font-semibold text-blue-700">₹{(revenueBreakdown?.totals.opd || 0).toLocaleString()}</p>
                </div>
                <div className="p-3 bg-orange-50 rounded-lg">
                  <p className="text-xs text-gray-500">Lab</p>
                  <p className="text-xl font-semibold text-orange-700">₹{(revenueBreakdown?.totals.lab || 0).toLocaleString()}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-xs text-gray-500">Pharma</p>
                  <p className="text-xl font-semibold text-green-700">₹{(revenueBreakdown?.totals.pharma || 0).toLocaleString()}</p>
                </div>
                <div className="p-3 bg-teal-50 rounded-lg">
                  <p className="text-xs text-gray-500">Additional Services</p>
                  <p className="text-xl font-semibold text-teal-700">
                    ₹{(revenueBreakdown?.totals.additionalServices ?? 0).toLocaleString()}
                  </p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-xs text-gray-500">IPD (discharged)</p>
                  <p className="text-xl font-semibold text-purple-700">₹{(revenueBreakdown?.totals.ipd || 0).toLocaleString()}</p>
                  <p className="text-[10px] text-gray-500 mt-1 leading-snug">
                    Counted when admission is discharged (by discharge date). Amount is the current IPD bill total and updates if the bill changes later.
                  </p>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg">
                  <p className="text-xs text-gray-500">DC (Day Care)</p>
                  <p className="text-xl font-semibold text-amber-700">₹{(revenueBreakdown?.totals.dc ?? 0).toLocaleString()}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-gray-500">Total</p>
                  <p className="text-xl font-semibold text-slate-700">
                    ₹{(
                      (revenueBreakdown?.totals.opd || 0) +
                      (revenueBreakdown?.totals.lab || 0) +
                      (revenueBreakdown?.totals.pharma || 0) +
                      (revenueBreakdown?.totals.additionalServices || 0) +
                      (revenueBreakdown?.totals.ipd || 0) +
                      (revenueBreakdown?.totals.dc || 0)
                    ).toLocaleString()}
                  </p>
                </div>
              </div>

              {revenueChart ? (
                <div className="bg-white rounded-lg border p-4">
                  <Line
                    data={revenueChart as any}
                    options={{
                      responsive: true,
                      plugins: { legend: { position: 'top' as const } },
                      interaction: { mode: 'index' as const, intersect: false },
                    }}
                  />
                </div>
              ) : (
                <p className="text-sm text-artis-slate">No data for selected date range.</p>
              )}
            </CardContent>
          </Card>
        )}

        {reportType === 'pharma_revenue' && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-green-700" />
                Pharma Revenue — Detailed
              </CardTitle>
              <p className="text-sm text-muted-foreground font-normal">
                Line-wise pharma collections for the selected range: pharmacy bill payments allocated to MEDICINE items
                plus IPD pharmacy collections (deposit purpose PHARMA_COLLECTION).
              </p>
            </CardHeader>
            <CardContent>
              {loadingPharmaRevenueLines ? (
                <p className="text-sm text-artis-slate">Loading…</p>
              ) : !pharmaRevenueLines?.rows?.length ? (
                <p className="text-sm text-artis-slate">No pharma revenue lines found for this range.</p>
              ) : (
                <div className="space-y-3">
                  <ReportSummaryTiles variant={summaryTilesVariant} tiles={pharmaRevenueSummaryTiles} />
                  <div className="flex items-center justify-between gap-3">
                    {pharmaRevenueLines.truncated && (
                      <p className="text-sm text-amber-700">
                        Preview may be truncated — use Download CSV for full data.
                      </p>
                    )}
                  </div>
                  <div className="rounded-lg border bg-white overflow-auto max-h-[560px]">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50 border-b z-10">
                        <tr>
                          <th className="text-left px-3 py-2">Date</th>
                          <th className="text-left px-3 py-2">Source</th>
                          <th className="text-left px-3 py-2">Bill / IPD No</th>
                          <th className="text-left px-3 py-2">Patient</th>
                          <th className="text-left px-3 py-2">UHID</th>
                          <th className="text-left px-3 py-2">Medicine</th>
                          <th className="text-right px-3 py-2">Qty</th>
                          <th className="text-left px-3 py-2">Batch</th>
                          <th className="text-left px-3 py-2">Payment mode</th>
                          <th className="text-right px-3 py-2">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pharmaRevenueLines.rows.map((r: any, idx: number) => (
                          <tr key={`${r.source}-${r.billNo}-${r.date}-${idx}`} className="border-b last:border-b-0 hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap">
                              {format(new Date(r.date), 'dd/MM/yyyy, hh:mm a')}
                            </td>
                            <td className="px-3 py-2 text-xs">
                              {r.source === 'IPD_PHARMA_COLLECTION' ? 'IPD pharma collection' : 'Bill payment'}
                            </td>
                            <td className="px-3 py-2 font-mono text-xs">{r.billNo}</td>
                            <td className="px-3 py-2">{r.patientName}</td>
                            <td className="px-3 py-2">{r.uhid ?? '—'}</td>
                            <td className="px-3 py-2">{r.medicineName || '—'}</td>
                            <td className="px-3 py-2 text-right">{r.quantity ?? '—'}</td>
                            <td className="px-3 py-2 text-xs">{r.batchNo || '—'}</td>
                            <td className="px-3 py-2">{r.paymentMode ?? '—'}</td>
                            <td className="px-3 py-2 text-right font-medium">
                              ₹{Number(r.amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {reportType === 'pharma_consumption' && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Pill className="w-5 h-5 text-purple-700" />
                Medicine Consumption by Drug
              </CardTitle>
              <p className="text-sm text-muted-foreground font-normal">
                Aggregated medicine usage across OPD pharmacy, IPD pharmacy, and OT for the selected period.
                Quantities are net of returns. Cost uses batch purchase rate; MRP uses issue/chargeable unit rate.
              </p>
            </CardHeader>
            <CardContent>
              {loadingPharmaConsumption ? (
                <p className="text-sm text-artis-slate">Loading…</p>
              ) : !pharmaConsumptionByDrug?.rows?.length ? (
                <p className="text-sm text-artis-slate">No medicine consumption found for this range.</p>
              ) : (
                <div className="space-y-3">
                  <ReportSummaryTiles variant={summaryTilesVariant} tiles={pharmaConsumptionSummaryTiles} />
                  <div className="rounded-lg border bg-white overflow-auto max-h-[560px]">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50 border-b z-10">
                        <tr>
                          <th className="text-left px-3 py-2">#</th>
                          <th className="text-left px-3 py-2">Medicine</th>
                          <th className="text-right px-3 py-2">Total qty</th>
                          <th className="text-right px-3 py-2">Cost (₹)</th>
                          <th className="text-right px-3 py-2">MRP (₹)</th>
                          <th className="text-right px-3 py-2">OPD qty</th>
                          <th className="text-right px-3 py-2">IPD qty</th>
                          <th className="text-right px-3 py-2">OT qty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pharmaConsumptionByDrug.rows.map((r, idx) => (
                          <tr key={r.drugId} className="border-b last:border-b-0 hover:bg-gray-50">
                            <td className="px-3 py-2 text-muted-foreground">{idx + 1}</td>
                            <td className="px-3 py-2">
                              <div className="font-medium">{r.medicineName}</div>
                              {(r.genericName || r.brandName || r.strength || r.form) && (
                                <div className="text-xs text-muted-foreground">
                                  {[r.genericName, r.brandName, r.strength, r.form].filter(Boolean).join(' · ')}
                                </div>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {Number(r.totalQty).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                              {r.unit ? ` ${r.unit}` : ''}
                            </td>
                            <td className="px-3 py-2 text-right">
                              ₹{Number(r.costValue).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-2 text-right font-medium">
                              ₹{Number(r.mrpValue).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-2 text-right text-muted-foreground">
                              {Number(r.opdQty).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-2 text-right text-muted-foreground">
                              {Number(r.ipdQty).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-2 text-right text-muted-foreground">
                              {Number(r.otQty).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {pharmaConsumptionByDrug.rows.length >= 500 && (
                    <p className="text-sm text-amber-700">
                      Preview shows top 500 medicines by MRP — use Download CSV for the full list.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {reportType === 'lab_billing' && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FlaskConical className="w-5 h-5 text-orange-600" />
                Lab Billing — Detailed
              </CardTitle>
              <p className="text-sm text-muted-foreground font-normal">
                Test-wise lab billing for the selected range: each row is one lab test line on an OPD/Day Care bill (by bill
                date) or an IPD bill (by service date).
              </p>
            </CardHeader>
            <CardContent>
              {loadingLabBillingLines ? (
                <p className="text-sm text-artis-slate">Loading…</p>
              ) : !labBillingLines?.rows?.length ? (
                <p className="text-sm text-artis-slate">No lab billing lines found for this range.</p>
              ) : (
                <div className="space-y-3">
                  <ReportSummaryTiles variant={summaryTilesVariant} tiles={labBillingSummaryTiles} />
                  <div className="flex items-center justify-between gap-3">
                    {labBillingLines.truncated && (
                      <p className="text-sm text-amber-700">
                        Preview may be truncated — use Download CSV for full data.
                      </p>
                    )}
                  </div>
                  <div className="rounded-lg border bg-white overflow-auto max-h-[560px]">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50 border-b z-10">
                        <tr>
                          <th className="text-left px-3 py-2">Date</th>
                          <th className="text-left px-3 py-2">Source</th>
                          <th className="text-left px-3 py-2">Bill / IPD No</th>
                          <th className="text-left px-3 py-2">Patient</th>
                          <th className="text-left px-3 py-2">UHID</th>
                          <th className="text-left px-3 py-2">Test</th>
                          <th className="text-left px-3 py-2">Code</th>
                          <th className="text-left px-3 py-2">Sample</th>
                          <th className="text-left px-3 py-2">Status</th>
                          <th className="text-right px-3 py-2">Qty</th>
                          <th className="text-right px-3 py-2">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {labBillingLines.rows.map((r, idx) => (
                          <tr key={`${r.source}-${r.billNo}-${r.testCode}-${r.date}-${idx}`} className="border-b last:border-b-0 hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap">
                              {format(new Date(r.date), 'dd/MM/yyyy, hh:mm a')}
                            </td>
                            <td className="px-3 py-2 text-xs">
                              {r.source === 'IPD_BILL'
                                ? 'IPD bill'
                                : r.source === 'DAY_CARE_BILL'
                                  ? 'Day care bill'
                                  : 'OPD bill'}
                            </td>
                            <td className="px-3 py-2 font-mono text-xs">{r.billNo}</td>
                            <td className="px-3 py-2">{r.patientName}</td>
                            <td className="px-3 py-2">{r.uhid ?? '—'}</td>
                            <td className="px-3 py-2">{r.testName || '—'}</td>
                            <td className="px-3 py-2 font-mono text-xs">{r.testCode || '—'}</td>
                            <td className="px-3 py-2 text-xs">{r.sampleType || '—'}</td>
                            <td className="px-3 py-2 text-xs">{r.testStatus || '—'}</td>
                            <td className="px-3 py-2 text-right">{r.quantity}</td>
                            <td className="px-3 py-2 text-right font-medium">
                              ₹{Number(r.amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {reportType === 'gst_filing' && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Receipt className="w-5 h-5 text-violet-600" />
                GST Filing Report
              </CardTitle>
              <p className="text-sm text-muted-foreground font-normal">
                Output GST on billed lines for the selected range: IPD bill items (by service date) and OPD / Day Care bill
                items (by bill date). OPD pharmacy uses MRP-inclusive GST derivation when only the rate is stored. CGST and
                SGST are split 50/50 (intra-state assumption). Download CSV for full filing detail.
              </p>
            </CardHeader>
            <CardContent>
              {loadingGstFiling ? (
                <p className="text-sm text-artis-slate">Loading…</p>
              ) : !gstFilingReport?.rows?.length ? (
                <p className="text-sm text-artis-slate">No GST lines found for this range.</p>
              ) : (
                <div className="space-y-4">
                  <ReportSummaryTiles variant={summaryTilesVariant} tiles={gstFilingSummaryTiles} />
                  {gstFilingReport.summary.byRate.length > 0 && (
                    <div className="rounded-lg border bg-slate-50/80 p-3">
                      <p className="text-xs font-medium text-slate-700 mb-2">GST by rate</p>
                      <div className="flex flex-wrap gap-3 text-xs">
                        {gstFilingReport.summary.byRate.map((r) => (
                          <span key={r.gstRate} className="rounded-md border bg-white px-2 py-1 tabular-nums">
                            {r.gstRate}% — ₹{r.gstAmount.toLocaleString('en-IN')} ({r.lineCount} lines)
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {gstFilingReport.truncated && (
                    <p className="text-sm text-amber-700">
                      Preview may be truncated — use Download CSV for full data.
                    </p>
                  )}
                  <div className="rounded-lg border bg-white overflow-auto max-h-[560px]">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50 border-b z-10">
                        <tr>
                          <th className="text-left px-3 py-2">Date</th>
                          <th className="text-left px-3 py-2">Module</th>
                          <th className="text-left px-3 py-2">Bill No</th>
                          <th className="text-left px-3 py-2">Patient</th>
                          <th className="text-left px-3 py-2">Description</th>
                          <th className="text-left px-3 py-2">HSN/SAC</th>
                          <th className="text-right px-3 py-2">Taxable</th>
                          <th className="text-right px-3 py-2">GST %</th>
                          <th className="text-right px-3 py-2">CGST</th>
                          <th className="text-right px-3 py-2">SGST</th>
                          <th className="text-right px-3 py-2">Total GST</th>
                        </tr>
                      </thead>
                      <tbody>
                        {gstFilingReport.rows.map((r) => (
                          <tr key={r.id} className="border-b last:border-b-0 hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap">
                              {format(new Date(r.date), 'dd/MM/yyyy')}
                            </td>
                            <td className="px-3 py-2 text-xs">{r.module}</td>
                            <td className="px-3 py-2 font-mono text-xs">{r.billNo}</td>
                            <td className="px-3 py-2">{r.patientName}</td>
                            <td className="px-3 py-2 max-w-[200px] truncate" title={r.description}>
                              {r.description}
                            </td>
                            <td className="px-3 py-2 font-mono text-xs">{r.hsnSac || '—'}</td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              ₹{r.taxableValue.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">{r.gstRate}%</td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              ₹{r.cgst.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              ₹{r.sgst.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums font-medium">
                              ₹{r.gstAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {reportType === 'additional_services' && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Layers className="w-5 h-5 text-teal-600" />
                Additional Services — Detailed
              </CardTitle>
              <p className="text-sm text-muted-foreground font-normal">
                Service-wise billing lines for Additional Services bills (SVC) by bill date.{' '}
                <strong>Collected (revenue)</strong> uses payment date (EMI installments). Paid and due on each line are
                bill-level totals repeated per service row.
              </p>
            </CardHeader>
            <CardContent>
              {loadingAdditionalServicesBilling ? (
                <p className="text-sm text-artis-slate">Loading…</p>
              ) : !additionalServicesBilling?.rows?.length ? (
                <p className="text-sm text-artis-slate">No Additional Services billing lines found for this range.</p>
              ) : (
                <div className="space-y-3">
                  <ReportSummaryTiles variant={summaryTilesVariant} tiles={additionalServicesSummaryTiles} />
                  <div className="flex items-center justify-between gap-3">
                    {additionalServicesBilling.truncated && (
                      <p className="text-sm text-amber-700">
                        Preview may be truncated — use Download CSV for full data.
                      </p>
                    )}
                  </div>
                  <div className="rounded-lg border bg-white overflow-auto max-h-[560px]">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50 border-b z-10">
                        <tr>
                          <th className="text-left px-3 py-2">Date</th>
                          <th className="text-left px-3 py-2">Bill No</th>
                          <th className="text-left px-3 py-2">Patient</th>
                          <th className="text-left px-3 py-2">Doctor</th>
                          <th className="text-left px-3 py-2">UHID</th>
                          <th className="text-left px-3 py-2">Phone</th>
                          <th className="text-left px-3 py-2">Service</th>
                          <th className="text-right px-3 py-2">Qty</th>
                          <th className="text-right px-3 py-2">Amount</th>
                          <th className="text-right px-3 py-2">Paid</th>
                          <th className="text-right px-3 py-2">Due</th>
                          <th className="text-left px-3 py-2">Payment Modes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {additionalServicesBilling.rows.map((r, idx) => (
                          <tr key={`${r.billId}-${r.serviceName}-${idx}`} className="border-b last:border-b-0 hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap">
                              {formatReportDateTimeIst(r.date)}
                            </td>
                            <td className="px-3 py-2 font-mono text-xs">{r.billNo}</td>
                            <td className="px-3 py-2">{r.patientName}</td>
                            <td className="px-3 py-2">{r.doctorName?.trim() || '—'}</td>
                            <td className="px-3 py-2">{r.uhid ?? '—'}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{r.phone ?? '—'}</td>
                            <td className="px-3 py-2">{r.serviceName || '—'}</td>
                            <td className="px-3 py-2 text-right">{r.quantity}</td>
                            <td className="px-3 py-2 text-right font-medium">
                              ₹{Number(r.amount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              ₹{Number(r.paidAmount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              ₹{Number(r.dueAmount || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-3 py-2 whitespace-nowrap">{r.paymentModes || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {reportType === 'patients' && (
          <div className="space-y-6">
            {!fromIso || !toIso ? (
              <p className="text-sm text-artis-slate">Select a date range above to load patient analytics.</p>
            ) : null}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5 text-purple-600" />
                  New Patients (Date Range)
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">New patients</p>
                  <p className="text-2xl font-bold">{newPatients?.total ?? 0}</p>
                </div>
                {newPatientsChart ? (
                  <div className="bg-white rounded-lg border p-4">
                    <Bar data={newPatientsChart as any} options={{ responsive: true, plugins: { legend: { display: false } } }} />
                  </div>
                ) : (
                  <p className="text-sm text-artis-slate">No data for selected date range.</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-blue-600" />
                  Patient Source Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-white rounded-lg border p-4">
                  {patientSourcesChart ? (
                    <Doughnut data={patientSourcesChart as any} options={{ responsive: true, plugins: { legend: { position: 'bottom' as const } } }} />
                  ) : (
                    <p className="text-sm text-artis-slate">No data for selected date range.</p>
                  )}
                </div>
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700">Top referral names</p>
                  <div className="rounded-lg border bg-white">
                    <div className="max-h-64 overflow-auto">
                      <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-gray-50 border-b">
                          <tr>
                            <th className="text-left px-3 py-2">Referral</th>
                            <th className="text-right px-3 py-2">Patients</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(patientSources?.referrals || []).slice(0, 20).map((r) => (
                            <tr key={r.name} className="border-b last:border-b-0">
                              <td className="px-3 py-2">{r.name}</td>
                              <td className="px-3 py-2 text-right">{r.count}</td>
                            </tr>
                          ))}
                          {(patientSources?.referrals || []).length === 0 && (
                            <tr>
                              <td className="px-3 py-4 text-gray-500" colSpan={2}>No referrals in this range.</td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <IndianRupee className="w-5 h-5 text-emerald-600" />
                  All patients revenue (date range)
                </CardTitle>
                <p className="text-sm text-artis-slate font-normal">
                  Per-patient OPD, Lab, Pharma, Additional Services, IPD, DC, and ICU revenue for the selected period. Only patients with at least one bill in range are listed.
                </p>
              </CardHeader>
              <CardContent>
                {loadingPatientRevenue ? (
                  <p className="text-sm text-artis-slate">Loading patient revenue...</p>
                ) : (
                  <div className="rounded-lg border bg-white overflow-auto max-h-[420px]">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50 border-b z-10">
                        <tr>
                          <th className="text-left px-3 py-2">Patient name</th>
                          <th className="text-left px-3 py-2">UHID</th>
                          <th className="text-left px-3 py-2">Phone</th>
                          {user?.role === 'SUPER_ADMIN' && <th className="text-left px-3 py-2">Hospital</th>}
                          <th className="text-right px-3 py-2">OPD</th>
                          <th className="text-right px-3 py-2">Lab</th>
                          <th className="text-right px-3 py-2">Pharma</th>
                          <th className="text-right px-3 py-2">Addl. Services</th>
                          <th className="text-right px-3 py-2">IPD</th>
                          <th className="text-right px-3 py-2">DC</th>
                          <th className="text-right px-3 py-2">ICU</th>
                          <th className="text-right px-3 py-2 font-medium">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(patientRevenueAll?.patients || []).map((row) => (
                          <tr key={row.patientId} className="border-b last:border-b-0 hover:bg-gray-50">
                            <td className="px-3 py-2">{row.patientName}</td>
                            <td className="px-3 py-2">{row.uhid ?? '—'}</td>
                            <td className="px-3 py-2">{row.phone ?? '—'}</td>
                            {user?.role === 'SUPER_ADMIN' && <td className="px-3 py-2">{row.hospitalName ?? '—'}</td>}
                            <td className="px-3 py-2 text-right">₹{Number(row.opd ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                            <td className="px-3 py-2 text-right">₹{Number(row.lab ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                            <td className="px-3 py-2 text-right">₹{Number(row.pharma ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                            <td className="px-3 py-2 text-right">₹{Number(row.additionalServices ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                            <td className="px-3 py-2 text-right">₹{Number(row.ipd ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                            <td className="px-3 py-2 text-right">₹{Number(row.dc ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                            <td className="px-3 py-2 text-right">₹{Number(row.icu ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                            <td className="px-3 py-2 text-right font-medium">₹{Number(row.total ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                          </tr>
                        ))}
                        {(!patientRevenueAll?.patients?.length) && (
                          <tr>
                            <td className="px-3 py-6 text-gray-500 text-center" colSpan={user?.role === 'SUPER_ADMIN' ? 12 : 11}>
                              No patients with revenue in the selected date range.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {reportType === 'patient_billing' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-emerald-600" />
                Patient-wise Billing Detail
              </CardTitle>
              <p className="text-sm text-artis-slate font-normal">
                {billingDetailView === 'summary'
                  ? 'Per-patient totals (OPD, Lab, Pharma, Additional Services, IPD, DC, ICU, Total). Filter by type to see only that category.'
                  : 'Item-wise: every line item per patient — OPD (consultation, procedure, etc.), Lab (each test), Pharma (each medicine), Additional Services, DC, IPD (Consultant, Visit, Room, Consumables, etc.), ICU (same item types).'}
              </p>
            </CardHeader>
            <CardContent>
              {billingDetailView === 'summary' ? (
                loadingBillingDetail ? (
                  <p className="text-sm text-artis-slate">Loading billing detail...</p>
                ) : (
                  <>
                  <ReportSummaryTiles variant={summaryTilesVariant} tiles={patientBillingSummaryTiles} />
                  <div className="rounded-lg border bg-white overflow-auto max-h-[520px]">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50 border-b z-10">
                        <tr>
                          <th className="text-left px-3 py-2">Patient name</th>
                          <th className="text-left px-3 py-2">UHID</th>
                          <th className="text-left px-3 py-2">Phone</th>
                          {user?.role === 'SUPER_ADMIN' && <th className="text-left px-3 py-2">Hospital</th>}
                          <th className="text-right px-3 py-2">OPD</th>
                          <th className="text-right px-3 py-2">Lab</th>
                          <th className="text-right px-3 py-2">Pharma</th>
                          <th className="text-right px-3 py-2">Addl. Services</th>
                          <th className="text-right px-3 py-2">IPD</th>
                          <th className="text-right px-3 py-2">DC</th>
                          <th className="text-right px-3 py-2">ICU</th>
                          <th className="text-right px-3 py-2 font-medium">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(patientBillingDetail?.patients || []).map((row) => (
                          <tr key={row.patientId} className="border-b last:border-b-0 hover:bg-gray-50">
                            <td className="px-3 py-2">{row.patientName}</td>
                            <td className="px-3 py-2">{row.uhid ?? '—'}</td>
                            <td className="px-3 py-2">{row.phone ?? '—'}</td>
                            {user?.role === 'SUPER_ADMIN' && <td className="px-3 py-2">{row.hospitalName ?? '—'}</td>}
                            <td className="px-3 py-2 text-right">₹{row.opd.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                            <td className="px-3 py-2 text-right">₹{row.lab.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                            <td className="px-3 py-2 text-right">₹{row.pharma.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                            <td className="px-3 py-2 text-right">₹{(row.additionalServices ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                            <td className="px-3 py-2 text-right">₹{row.ipd.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                            <td className="px-3 py-2 text-right">₹{(row.dc ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                            <td className="px-3 py-2 text-right">₹{(row.icu ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                            <td className="px-3 py-2 text-right font-medium">₹{row.total.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                          </tr>
                        ))}
                        {(!patientBillingDetail?.patients?.length) && (
                          <tr>
                            <td className="px-3 py-6 text-gray-500 text-center" colSpan={user?.role === 'SUPER_ADMIN' ? 12 : 11}>
                              No patients with billing in the selected date range and type.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  </>
                )
              ) : loadingBillingDetailItems ? (
                <p className="text-sm text-artis-slate">Loading item-wise billing...</p>
              ) : (
                <div className="rounded-lg border bg-white overflow-auto max-h-[560px]">
                  <table className="w-full text-sm">
                    <thead className="sticky top-0 bg-gray-50 border-b z-10">
                      <tr>
                        <th className="text-left px-3 py-2">Patient name</th>
                        <th className="text-left px-3 py-2">UHID</th>
                        <th className="text-left px-3 py-2">Phone</th>
                        {user?.role === 'SUPER_ADMIN' && <th className="text-left px-3 py-2">Hospital</th>}
                        <th className="text-left px-3 py-2">Category</th>
                        <th className="text-left px-3 py-2">Item type</th>
                        <th className="text-left px-3 py-2">Item description</th>
                        <th className="text-left px-3 py-2">Charge code</th>
                        <th className="text-right px-3 py-2">Qty</th>
                        <th className="text-right px-3 py-2">Unit price</th>
                        <th className="text-right px-3 py-2">Amount</th>
                        <th className="text-left px-3 py-2">Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(patientBillingDetailItems?.items || []).map((row, idx) => (
                        <tr key={`${row.patientId}-${row.category}-${row.itemDescription}-${idx}`} className="border-b last:border-b-0 hover:bg-gray-50">
                          <td className="px-3 py-2">{row.patientName}</td>
                          <td className="px-3 py-2">{row.uhid ?? '—'}</td>
                          <td className="px-3 py-2">{row.phone ?? '—'}</td>
                          {user?.role === 'SUPER_ADMIN' && <td className="px-3 py-2">{row.hospitalName ?? '—'}</td>}
                          <td className="px-3 py-2">{row.category}</td>
                          <td className="px-3 py-2">{row.itemType.replace(/_/g, ' ')}</td>
                          <td className="px-3 py-2 max-w-[200px] truncate" title={row.itemDescription}>{row.itemDescription}</td>
                          <td className="px-3 py-2">{row.chargeCode ?? '—'}</td>
                          <td className="px-3 py-2 text-right">{row.quantity}</td>
                          <td className="px-3 py-2 text-right">₹{row.unitPrice.toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                          <td className="px-3 py-2 text-right">₹{row.amount.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                          <td className="px-3 py-2">{row.serviceDate.slice(0, 10)}</td>
                        </tr>
                      ))}
                      {(!patientBillingDetailItems?.items?.length) && (
                        <tr>
                          <td className="px-3 py-6 text-gray-500 text-center" colSpan={user?.role === 'SUPER_ADMIN' ? 12 : 11}>
                            No billing items in the selected date range and type.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {reportType === 'patient_master' && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600" />
                Patient Master
              </CardTitle>
              <p className="text-sm text-muted-foreground font-normal">
                One row per patient with demographics, family links, visit counts, and billing totals.
                {patientMasterScope === 'all'
                  ? ' Showing all patients with lifetime visit and billing totals.'
                  : patientMasterScope === 'registered'
                    ? ' Showing patients registered in the selected date range.'
                    : ' Showing patients with OPD, IPD, day care, or billing activity in the selected date range.'}
              </p>
            </CardHeader>
            <CardContent>
              {loadingPatientMaster ? (
                <p className="text-sm text-artis-slate">Loading…</p>
              ) : (
                <>
                  <ReportSummaryTiles variant={summaryTilesVariant} tiles={patientMasterSummaryTiles} />
                  {patientMasterData?.truncated && (
                    <p className="text-sm text-amber-700 mb-2">
                      Showing first 500 patients in preview — use Download CSV for the full list (
                      {patientMasterData.totalPatients.toLocaleString('en-IN')} patients).
                    </p>
                  )}
                  <div className="rounded-lg border bg-white overflow-auto max-h-[560px]">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50 border-b z-10">
                        <tr>
                          <th className="text-left px-3 py-2">UHID</th>
                          <th className="text-left px-3 py-2">Patient</th>
                          <th className="text-left px-3 py-2">Phone</th>
                          <th className="text-left px-3 py-2">Email</th>
                          <th className="text-left px-3 py-2">Gender</th>
                          <th className="text-right px-3 py-2">Age</th>
                          <th className="text-left px-3 py-2">Source</th>
                          <th className="text-left px-3 py-2">Referrer</th>
                          <th className="text-left px-3 py-2">Family</th>
                          <th className="text-right px-3 py-2">Visits</th>
                          <th className="text-right px-3 py-2">Total billing</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(patientMasterData?.rows || []).map((row) => (
                          <tr key={row.patientId} className="border-b last:border-b-0 hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap">{row.uhid || '—'}</td>
                            <td className="px-3 py-2">{row.patientName}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{row.phone}</td>
                            <td className="px-3 py-2">{row.email || '—'}</td>
                            <td className="px-3 py-2">{row.gender}</td>
                            <td className="px-3 py-2 text-right tabular-nums">{row.ageYears ?? '—'}</td>
                            <td className="px-3 py-2">{row.registrationSource}</td>
                            <td className="px-3 py-2">{row.referrerName || '—'}</td>
                            <td className="px-3 py-2 max-w-[200px] truncate" title={row.familyDetails || undefined}>
                              {row.familyDetails || '—'}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">{row.totalVisits}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-medium">
                              ₹{row.totalBilling.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                            </td>
                          </tr>
                        ))}
                        {(!patientMasterData?.rows?.length) && (
                          <tr>
                            <td className="px-3 py-6 text-gray-500 text-center" colSpan={11}>
                              No patients match the selected scope and date range.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-muted-foreground mt-3">
                    CSV download includes full address, clinical notes, emergency contacts, visit breakdown
                    (OPD / IPD / day care), and billing split by OPD, lab, pharma, IPD, day care, and ICU.
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {reportType === 'bill_wise' && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Bill-wise report
              </CardTitle>
              <p className="text-sm text-muted-foreground font-normal">
                One row per bill across OPD, Lab, Pharma, Additional Services, Day Care, IPD, ICU, and separately billed IPD Pharma.
                Pharma on the main IPD bill stays on the IPD row only. Outpatient bills use bill date;
                IPD/ICU bills use discharge date (any bill status once the patient is discharged).
              </p>
            </CardHeader>
            <CardContent>
              {loadingBillWise ? (
                <p className="text-sm text-artis-slate">Loading…</p>
              ) : (
                <>
                  <ReportSummaryTiles variant={summaryTilesVariant} tiles={billWiseSummaryTiles} />
                  <div className="rounded-lg border bg-white overflow-auto max-h-[560px]">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50 border-b z-10">
                        <tr>
                          <th className="text-left px-3 py-2">Category</th>
                          <th className="text-left px-3 py-2">Bill no.</th>
                          <th className="text-left px-3 py-2">Date</th>
                          <th className="text-left px-3 py-2">Patient</th>
                          <th className="text-left px-3 py-2">UHID</th>
                          <th className="text-right px-3 py-2">Net</th>
                          <th className="text-right px-3 py-2">Paid</th>
                          <th className="text-right px-3 py-2">Due</th>
                          <th className="text-left px-3 py-2">Status</th>
                          <th className="text-left px-3 py-2">Payment</th>
                          <th className="text-left px-3 py-2">IPD no.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(billWiseReport?.bills || []).map((row) => (
                          <tr key={`${row.billCategory}-${row.billId}`} className="border-b last:border-b-0 hover:bg-gray-50">
                            <td className="px-3 py-2 whitespace-nowrap">{row.billCategory}</td>
                            <td className="px-3 py-2 whitespace-nowrap font-medium">{row.billNumber}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {format(new Date(row.billDate), 'dd/MM/yyyy')}
                            </td>
                            <td className="px-3 py-2">{row.patientName}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{row.uhid || '—'}</td>
                            <td className="px-3 py-2 text-right tabular-nums">₹{row.netAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2 text-right tabular-nums">₹{row.paidAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2 text-right tabular-nums">₹{row.dueAmount.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{row.status}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{row.paymentModes || '—'}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{row.ipdNumber || '—'}</td>
                          </tr>
                        ))}
                        {(!billWiseReport?.bills?.length) && (
                          <tr>
                            <td className="px-3 py-6 text-gray-500 text-center" colSpan={11}>
                              No bills in the selected date range.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {reportType === 'opd_payment_lines' && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-emerald-600" />
                OPD payment lines
              </CardTitle>
              <p className="text-sm text-muted-foreground font-normal">
                One row per patient per day with OPD, lab, and pharma revenue combined (same calendar day in IST).
                Source comes from the linked appointment (Practo, WEBSITE, META, etc.) or{' '}
                <span className="font-medium">WALK_IN</span> when not set.
              </p>
            </CardHeader>
            <CardContent>
              {loadingOpdPaymentLines ? (
                <p className="text-sm text-artis-slate">Loading…</p>
              ) : (
                <>
                  <ReportSummaryTiles variant={summaryTilesVariant} tiles={opdPaymentSummaryTiles} />
                  {opdPaymentLinesData?.truncated && (
                    <p className="text-sm text-amber-700 mb-2">
                      Showing first 3,000 patient-days in preview — use Download CSV for the full list.
                    </p>
                  )}
                  <div className="rounded-lg border bg-white overflow-auto max-h-[520px]">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50 border-b z-10">
                        <tr>
                          <th className="text-left px-3 py-2">UHID NO</th>
                          <th className="text-left px-3 py-2">Patient NAME</th>
                          <th className="text-left px-3 py-2">Mobile number</th>
                          <th className="text-left px-3 py-2">Doctor NAME</th>
                          <th className="text-left px-3 py-2">Payment mode</th>
                          <th className="text-right px-3 py-2">OPD revenue</th>
                          <th className="text-right px-3 py-2">Actual OPD bill</th>
                          <th className="text-right px-3 py-2">Lab revenue</th>
                          <th className="text-right px-3 py-2">Actual Lab bill</th>
                          <th className="text-right px-3 py-2">Pharma revenue</th>
                          <th className="text-right px-3 py-2">Actual Pharma bill</th>
                          <th className="text-right px-3 py-2">Total</th>
                          <th className="text-right px-3 py-2">Actual bill total</th>
                          <th className="text-left px-3 py-2">DATE</th>
                          <th className="text-left px-3 py-2">OPD Source</th>
                          <th className="text-left px-3 py-2">OPD payout</th>
                          <th className="text-right px-3 py-2">Payout amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(opdPaymentLinesData?.rows || []).map((row, idx) => (
                          <tr key={`${row.serviceDay ?? row.date}-${idx}-${row.uhidNo}`} className="border-b last:border-b-0 hover:bg-gray-50">
                            <td className="px-3 py-2">{row.uhidNo || '—'}</td>
                            <td className="px-3 py-2">{row.patientName}</td>
                            <td className="px-3 py-2 whitespace-nowrap">{row.mobile || '—'}</td>
                            <td className="px-3 py-2">{row.doctorName || '—'}</td>
                            <td className="px-3 py-2">{row.paymentMode}</td>
                            <td className="px-3 py-2 text-right tabular-nums">₹{(row.opdAmount ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">₹{(row.actualOpdAmount ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2 text-right tabular-nums">₹{(row.labAmount ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">₹{(row.actualLabAmount ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2 text-right tabular-nums">₹{(row.pharmaAmount ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">₹{(row.actualPharmaAmount ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-medium">₹{(row.totalAmount ?? row.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-medium text-muted-foreground">₹{(row.actualBillNet ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {formatReportServiceDay(row.serviceDay, row.date)}
                            </td>
                            <td className="px-3 py-2">{row.opdSource}</td>
                            <td className="px-3 py-2">
                              {row.opdPayoutRecorded ? (
                                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                                  Yes
                                </span>
                              ) : (
                                <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                                  No
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">
                              {row.opdPayoutAmount != null && row.opdPayoutAmount > 0
                                ? `₹${row.opdPayoutAmount.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                                : '—'}
                            </td>
                          </tr>
                        ))}
                        {(!opdPaymentLinesData?.rows?.length) && (
                          <tr>
                            <td className="px-3 py-6 text-gray-500 text-center" colSpan={17}>
                              No OPD payments in the selected date range.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {reportType === 'daycare_payment_lines' && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <IndianRupee className="w-5 h-5 text-amber-600" />
                Day Care payment lines
              </CardTitle>
              <p className="text-sm text-muted-foreground font-normal">
                One row per patient per day with Day Care, lab, and pharma revenue combined. Doctor from the Day Care case.
              </p>
            </CardHeader>
            <CardContent>
              {loadingDayCarePaymentLines ? (
                <p className="text-sm text-artis-slate">Loading…</p>
              ) : (
                <>
                  <ReportSummaryTiles variant={summaryTilesVariant} tiles={dayCarePaymentSummaryTiles} />
                  {dayCarePaymentLinesData?.truncated && (
                    <p className="text-sm text-amber-700 mb-2">
                      Showing first 3,000 patient-days in preview — use Download CSV for the full list.
                    </p>
                  )}
                  <div className="rounded-lg border bg-white overflow-auto max-h-[520px]">
                    <table className="w-full text-sm">
                      <thead className="sticky top-0 bg-gray-50 border-b z-10">
                        <tr>
                          <th className="text-left px-3 py-2">UHID NO</th>
                          <th className="text-left px-3 py-2">Patient NAME</th>
                          <th className="text-left px-3 py-2">Doctor NAME</th>
                          <th className="text-left px-3 py-2">Payment mode</th>
                          <th className="text-right px-3 py-2">DC revenue</th>
                          <th className="text-right px-3 py-2">Lab revenue</th>
                          <th className="text-right px-3 py-2">Pharma revenue</th>
                          <th className="text-right px-3 py-2">Total</th>
                          <th className="text-left px-3 py-2">DATE</th>
                          <th className="text-left px-3 py-2">Source</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(dayCarePaymentLinesData?.rows || []).map((row, idx) => (
                          <tr key={`${row.serviceDay ?? row.date}-${idx}-${row.patientName}`} className="border-b last:border-b-0 hover:bg-gray-50">
                            <td className="px-3 py-2">{row.uhidNo || '—'}</td>
                            <td className="px-3 py-2">{row.patientName}</td>
                            <td className="px-3 py-2">{row.doctorName || '—'}</td>
                            <td className="px-3 py-2">{row.paymentMode}</td>
                            <td className="px-3 py-2 text-right tabular-nums">₹{(row.opdAmount ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2 text-right tabular-nums">₹{(row.labAmount ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2 text-right tabular-nums">₹{(row.pharmaAmount ?? 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2 text-right tabular-nums font-medium">₹{(row.totalAmount ?? row.amount).toLocaleString(undefined, { maximumFractionDigits: 2 })}</td>
                            <td className="px-3 py-2 whitespace-nowrap">
                              {formatReportServiceDay(row.serviceDay, row.date)}
                            </td>
                            <td className="px-3 py-2">{row.opdSource}</td>
                          </tr>
                        ))}
                        {(!dayCarePaymentLinesData?.rows?.length) && (
                          <tr>
                            <td className="px-3 py-6 text-gray-500 text-center" colSpan={10}>
                              No Day Care payments in the selected date range.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {reportType === 'summary' && (
          <BusinessReportsDashboard
            data={dashboardAnalytics}
            isLoading={loadingDashboard}
            isError={dashboardError}
            errorMessage={dashboardErrorMessage}
            granularity={chartGranularity}
            onGranularityChange={setChartGranularity}
            investorMode={isInvestor}
            chainConsolidated={reportHospitalId === 'all'}
          />
        )}

        {(reportType === 'summary' || reportType === 'revenue') && (
          <>
            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <IndianRupee className="w-5 h-5 text-emerald-600" />
                  Revenue
                </CardTitle>
              </CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <p className="text-sm text-artis-slate">Total Revenue</p>
                  <p className="text-2xl font-bold tabular-nums text-artis-brown-dark">
                    ₹{(kpis?.totalRevenue || 0).toLocaleString()}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-artis-slate">Average per Appointment</p>
                  <p className="text-2xl font-bold tabular-nums text-artis-brown-dark">
                    ₹{(kpis?.averageRevenuePerAppointment || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-artis-slate">Total Appointments (billed)</p>
                  <p className="text-2xl font-bold tabular-nums text-artis-brown-dark">{kpis?.totalAppointments || 0}</p>
                </div>
              </CardContent>
            </Card>

            <Card className="mb-6">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <IndianRupee className="w-5 h-5 text-slate-600" />
                  Payment type
                </CardTitle>
                <p className="text-sm text-artis-slate font-normal">
                  Revenue by service type and payment method in the selected period (OPD, IPD, Pharma, Lab, DayCare, Additional Services, Emergency).
                </p>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto rounded-md border">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 border-b">
                        <th className="text-left px-3 py-2 font-medium text-gray-700">Service type</th>
                        <th className="text-right px-3 py-2 font-medium text-gray-700">Cash</th>
                        <th className="text-right px-3 py-2 font-medium text-gray-700">UPI</th>
                        <th className="text-right px-3 py-2 font-medium text-gray-700">Card</th>
                        <th className="text-right px-3 py-2 font-medium text-gray-700">Bank Transfer</th>
                        <th className="text-right px-3 py-2 font-medium text-gray-700">Insurance</th>
                        <th className="text-right px-3 py-2 font-medium text-gray-700">Cheque</th>
                        <th className="text-right px-3 py-2 font-medium text-gray-700">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paymentByServiceType?.table
                        ? PAYMENT_SERVICE_TYPES.map((row) => {
                            const r = paymentByServiceType.table[row];
                            if (!r) return null;
                            const cash = r.CASH ?? 0;
                            const upi = r.UPI ?? 0;
                            const card = r.CARD ?? 0;
                            const bank = r.BANK_TRANSFER ?? 0;
                            const insurance = r.INSURANCE ?? 0;
                            const cheque = r.CHEQUE ?? 0;
                            const total = cash + upi + card + bank + insurance + cheque;
                            return (
                              <tr key={row} className="border-b last:border-b-0 hover:bg-gray-50">
                                <td className="px-3 py-2 font-medium">{SERVICE_TYPE_LABELS[row]}</td>
                                <td className="px-3 py-2 text-right tabular-nums">₹{cash.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                <td className="px-3 py-2 text-right tabular-nums">₹{upi.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                <td className="px-3 py-2 text-right tabular-nums">₹{card.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                <td className="px-3 py-2 text-right tabular-nums">₹{bank.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                <td className="px-3 py-2 text-right tabular-nums">₹{insurance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                <td className="px-3 py-2 text-right tabular-nums">₹{cheque.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                <td className="px-3 py-2 text-right tabular-nums font-medium">₹{total.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                              </tr>
                            );
                            }).concat([
                              (() => {
                                const table = paymentByServiceType.table;
                                const rows = PAYMENT_SERVICE_TYPES
                                  .map((k) => table[k])
                                  .filter(Boolean) as Array<any>;
                                const cash = rows.reduce((s, r) => s + (r.CASH ?? 0), 0);
                                const upi = rows.reduce((s, r) => s + (r.UPI ?? 0), 0);
                                const card = rows.reduce((s, r) => s + (r.CARD ?? 0), 0);
                                const bank = rows.reduce((s, r) => s + (r.BANK_TRANSFER ?? 0), 0);
                                const insurance = rows.reduce((s, r) => s + (r.INSURANCE ?? 0), 0);
                                const cheque = rows.reduce((s, r) => s + (r.CHEQUE ?? 0), 0);
                                const total = cash + upi + card + bank + insurance + cheque;
                                return (
                                  <tr key="__total" className="border-t bg-slate-50 font-medium">
                                    <td className="px-3 py-2">Total</td>
                                    <td className="px-3 py-2 text-right tabular-nums">₹{cash.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">₹{upi.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">₹{card.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">₹{bank.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">₹{insurance.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">₹{cheque.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                    <td className="px-3 py-2 text-right tabular-nums">₹{total.toLocaleString(undefined, { maximumFractionDigits: 0 })}</td>
                                  </tr>
                                );
                              })(),
                            ])
                        : (
                          <tr>
                            <td colSpan={8} className="px-3 py-6 text-center text-gray-500">
                              Loading payment breakdown...
                            </td>
                          </tr>
                        )}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {reportType === 'appointments' && <ReportSummaryTiles variant={summaryTilesVariant} tiles={appointmentsSummaryTiles} />}

        {(reportType === 'summary' || reportType === 'appointments') && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-blue-600" />
                Appointments
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {reportType === 'summary' && <ReportSummaryTiles variant={summaryTilesVariant} tiles={appointmentsSummaryTiles} />}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Total</p>
                  <p className="text-xl font-semibold">{kpis?.totalAppointments || 0}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-xs text-gray-500">Completed</p>
                  <p className="text-xl font-semibold text-green-700">{kpis?.totalCompletedAppointments || 0}</p>
                </div>
                <div className="p-3 bg-red-50 rounded-lg">
                  <p className="text-xs text-gray-500">Cancelled</p>
                  <p className="text-xl font-semibold text-red-700">{kpis?.totalCancelledAppointments || 0}</p>
                </div>
                <div className="p-3 bg-indigo-50 rounded-lg">
                  <p className="text-xs text-gray-500">Follow-ups</p>
                  <p className="text-xl font-semibold text-indigo-700">{kpis?.totalFollowUps || 0}</p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600">Completion rate</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-green-500 rounded-full"
                      style={{ width: `${completionRate}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium">{completionRate}%</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {reportType === 'lab' && <ReportSummaryTiles variant={summaryTilesVariant} tiles={labSummaryTiles} />}

        {(reportType === 'summary' || reportType === 'lab') && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FlaskConical className="w-5 h-5 text-orange-600" />
                Lab Operations
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {reportType === 'summary' && <ReportSummaryTiles variant={summaryTilesVariant} tiles={labSummaryTiles} />}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">Total tests</p>
                  <p className="text-xl font-semibold">{kpis?.totalLabTests || 0}</p>
                </div>
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <p className="text-xs text-gray-500">Pending</p>
                  <p className="text-xl font-semibold text-yellow-700">{kpis?.pendingLabTests || 0}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-xs text-gray-500">Completed</p>
                  <p className="text-xl font-semibold text-green-700">
                    {(kpis?.totalLabTests || 0) - (kpis?.pendingLabTests || 0)}
                  </p>
                </div>
              </div>
              <div>
                <p className="text-sm text-gray-600">Lab completion rate</p>
                <div className="flex items-center gap-2 mt-1">
                  <div className="flex-1 h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-orange-500 rounded-full"
                      style={{ width: `${labCompletionRate}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium">{labCompletionRate}%</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {reportType === 'summary' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-600" />
                Patients & Staff
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-500">New patients (period)</p>
                  <p className="text-xl font-semibold">{kpis?.totalPatients || 0}</p>
                </div>
                <div className="p-3 bg-teal-50 rounded-lg">
                  <p className="text-xs text-gray-500">Active patients</p>
                  <p className="text-xl font-semibold text-teal-700">{kpis?.activePatients || 0}</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-lg">
                  <p className="text-xs text-gray-500">Total staff</p>
                  <p className="text-xl font-semibold text-purple-700">{kpis?.totalStaff || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Print footer */}
      <div className="hidden print:block mt-8 pt-4 border-t text-center text-sm text-artis-slate">
        Generated on {format(new Date(), 'PPpp')} — T.R.U.E. Hospitals Business Report
      </div>
    </div>
  );
}
